import {
  ActionIcon,
  Anchor,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Container,
  Divider,
  Grid,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  Stack,
  Switch,
  Tabs,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { DateInput } from '@mantine/dates';
import {
  calendarDateFromPickerValue,
  dateInputValueToDateOnly,
  formatDateOnlyLocal,
  parseDateOnlyLocal,
  toFhirDateOnlyLocal,
  formatUsDateShort,
} from '@/lib/dateHelpers';
import {
  IconBuilding,
  IconCalendar,
  IconCamera,
  IconDeviceFloppy,
  IconHeart,
  IconHome,
  IconLock,
  IconShield,
  IconStethoscope,
  IconUser,
  IconUsers,
  IconUsersGroup,
  IconActivity,
  IconCalendarEvent,
  IconEdit,
  IconInfoCircle,
  IconPlus,
} from '@tabler/icons-react';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type {
  Attachment,
  ContactPoint,
  Extension,
  Location as FhirLocation,
  Practitioner,
  PractitionerRole,
} from '@medplum/fhirtypes';
import {
  User as UserType,
  Role,
  ROLE_LABELS,
  heldRolesForUser,
  InsuranceCredential,
  Practice,
  type PracticeLocation,
} from '../../users/types/rbac';
import { formatLastActive } from '../../users/components/UserTable';
import { fetchAuthMeSnapshot, latestSessionLastSeen } from '../../../lib/securitySettingsService';
import { UserPermissionsTabPanel } from './UserPermissionsTabPanel';
import {
  DraftDobAndZip,
  DraftProfileDemographics,
  type DemographicsDraftHandle,
  type DobZipDraftHandle,
} from './UserSettingsDraftFields';
import { ProviderProfileSettingsFields } from './ProviderProfileSettingsFields';
import { ProviderCaqhSettingsFields } from './ProviderCaqhSettingsFields';
import {
  EMPTY_CERT_MODAL,
  EMPTY_DEA_MODAL,
  EMPTY_EDU_MODAL,
  EMPTY_LICENSE_MODAL,
  EMPTY_PROVIDER_CLINICAL,
  EMPTY_PROVIDER_PROFILE_TAB,
  EMPTY_SUPP_MODAL,
  EMPTY_TRAINING_MODAL,
} from './providerSettingsFormDefaults';
import { CredentialFormModal } from './CredentialFormModal';
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog';
import { LocationTileMap } from './LocationTileMap';
import { ProfileSignatureSettings } from './ProfileSignatureSettings';
import {
  createProfileAutosaveController,
  shouldApplyAutosaveResult,
  shouldSkipUnchangedProviderSave,
  shouldValidateDirtyForm,
  statusAfterAutosave,
  type ProfileAutosaveController,
} from './profileAutosave';
import {
  LICENSE_TYPES,
  TRAINING_TYPE_OPTIONS,
  US_STATES,
} from '../../onboarding/constants';
import { resolvePermissionsFromProfile, getAssignableRolesForHeldRoles, getAssignmentActorRole, isUserRoleFieldEditable, canEditorAssignRole, canEditMemberProfile, canViewRolePermissionsReference } from '../../../features/rbac';
import { setRole, setPrimaryRole } from '../../../lib/adminActionsService';
import { attachmentDataUrl, createSecuredAttachment } from '../../../lib/createSecuredAttachment';
import { PROFILE_RESOURCE_READ_OPTIONS } from '../../../lib/medplumDirectoryUsers';
import {
  RoleTransitionModal,
} from './RoleTransitionModal';
import {
  roleSetChangeNeedsTransitionWizard,
} from '../../../lib/roleTransitionPolicy';
import {
  derivePrimaryRole,
  formatCollapsedRoleLabels,
  holdsSupervisingCapability,
  holdsTreatingCapability,
  normalizeHeldRoles,
  sameRoleSet,
} from '@unify/fhir-model/role-display';
import { usePermissions } from '../../../contexts/PermissionContext';
import type { Client, ClientGroup } from '../../caseload/types';
import type { ProviderAssociation } from '../../clients/data';
import {
  useProviderSessionTypeEnablement,
  useProviderMeetingTypeEnablement,
  useProviderTimeEntryTypeEnablement,
  type AppointmentSessionType,
  type MeetingType as CatalogMeetingType,
  type TimeEntryType as CatalogTimeEntryType,
} from '../../calendar/appointmentTypeCatalog';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { SaveStatusIndicator, type SaveStatus } from '@/components/SaveStatusIndicator';
import { PractitionerNameText } from '@/components/PractitionerNameText';
import {
  boardCertModalSchema,
  deaModalSchema,
  educationModalSchema,
  licenseModalSchema,
  providerSettingsClinicalSchema,
  providerSettingsHeadshotFieldLabels,
  providerSettingsHeadshotSchema,
  providerSettingsProfileFieldLabels,
  providerSettingsProfileTabSchema,
  supplementalModalSchema,
  trainingModalSchema,
  type BoardCertModalFormValues,
  type DeaModalFormValues,
  type EducationModalFormValues,
  type LicenseModalFormValues,
  type ProviderSettingsClinicalFormValues,
  type ProviderSettingsProfileTabFormValues,
  type SupplementalModalFormValues,
  type TrainingModalFormValues,
} from '@/schemas/practitioner.schema';
import { missingFieldMessage } from '../../../lib/formValidation';
import {
  BIO_EXT,
  CAQH_EXT_URLS,
  CLINICAL_EXT,
  CREDENTIAL_TYPE_SYSTEM,
  EXT_URLS,
  GENDER_IDENTITY_EXT,
  LGBTQIA_IDENTITIES_EXT,
  NPI_IDENTIFIER_SYSTEM,
  QUALIFICATION_EXT_URLS,
  RACE_ETHNICITY_EXT,
  RELIGION_SPIRITUALITY_EXT,
} from '@unify/fhir-model/constants';
import { ADMIN_EXTENSION_URLS } from '../../../lib/adminDirectoryConstants';
import { fetchCreatedAtFromHistory } from '../../../lib/practitionerCreatedAt';
import { getLocationWorkPhone, isPracticeSiteLocation, parseLocationRefId } from '@unify/fhir-model/resources/location';
import {
  getBio,
  getCaqhAttestationDate,
  getCaqhId,
  getCaqhPassword,
  getCaqhUsername,
  getClinical,
  getPractitionerGenderIdentity,
  getPractitionerLgbtqiaIdentities,
  getPractitionerPronouns,
  getPractitionerRaceEthnicity,
  getPractitionerReligionSpirituality,
  getRole,
  setBio,
  setCaqhAttestationDate as setCaqhAttestationDateExtension,
  setCaqhId as setCaqhIdExtension,
  setCaqhPassword as setCaqhPasswordExtension,
  setCaqhUsername as setCaqhUsernameExtension,
  setClinical,
  formatPractitionerNameParts,
  resolvePractitionerLegalName,
  resolvePractitionerPreferredNamesForForm,
  stripPractitionerLegacyPreferredNames,
  upsertPractitionerNickname,
} from '@unify/fhir-model/resources/practitioner';
import { getPractitionerRoleCodes } from '@unify/fhir-model/resources/practitioner-role';
import { isUnifyPlatformStaffRole } from '@unify/fhir-model/staff-role-org';
import { applyClientLegalNameToUserUpdates } from './userSettingsSave';
import { FEATURE_VISIBILITY, isUnifyAdminShellRole } from '../../../config/featureVisibility';
import { LocalhostOnlyBadge } from '../../../components/LocalhostOnlyBadge';
import { rolesNeedZoomSeat } from '@/schemas/zoom.schema';

interface UserSettingsProps {
  user: UserType;
  onBack: () => void;
  onUpdateUser: (
    id: string,
    updates: Partial<UserType>,
    options?: {
      silentSuccess?: boolean;
      silentError?: boolean;
      throwOnError?: boolean;
      shouldApplyResult?: () => boolean;
    },
  ) => void | Promise<void>;
  autoSave?: boolean;
  onDeleteUser?: (id: string) => void;
  isPlatformView: boolean;
  allUsers: UserType[];
  practices: Practice[];
  /** When viewing a client, show Profile / Contacts / Insurance */
  client?: Client;
  clientGroups?: ClientGroup[];
  providers?: ProviderAssociation[];
  /** `permissions` renders only the assigned-roles panel for Settings → Security. */
  mode?: 'full' | 'permissions';
}

type License = {
  id: string;
  jurisdictionState: string;
  licenseName: string;
  licenseNumber: string;
  expirationDate: string;
  licenseStatus: string;
  primaryLicense: boolean;
  lastModifiedDate: string;
  issueDate?: string;
  licenseType?: string;
  superseded?: boolean;
};

type Certification = {
  id: string;
  boardCertificationName: string;
  certificationNumber: string;
  certificationDate: string;
  certificateFileName: string;
  certType?: string;
};

type Education = {
  id: string;
  institution: string;
  degree: string;
  graduationYear: string;
};

type DeaCredential = {
  id: string;
  deaNumberName: string;
  active: boolean;
  expirationDate: string;
  drugSchedules: string;
  businessActivity: string;
};

type SupplementalCredential = {
  id: string;
  trainingName: string;
  completedDate: string;
  trainingType: string;
};

type Training = {
  id: string;
  trainingType: string;
  completedDate: string;
};

type Qualification = NonNullable<Practitioner['qualification']>[number];
async function collectPractitionerRoleSlugs(
  medplum: ReturnType<typeof useMedplum>,
  practitionerRoles: PractitionerRole[],
  orgRefFromPractitioner: string | undefined,
): Promise<string[]> {
  const active = practitionerRoles.filter((r) => r.active !== false);
  const orgScoped = orgRefFromPractitioner
    ? active.find((r) => r.organization?.reference === orgRefFromPractitioner)
    : undefined;

  const readCodes = async (pr: PractitionerRole | undefined): Promise<string[]> => {
    const fromSearch = getPractitionerRoleCodes(pr);
    if (fromSearch.length > 0 || !pr?.id) return fromSearch;
    try {
      return getPractitionerRoleCodes(await medplum.readResource('PractitionerRole', pr.id));
    } catch {
      return [];
    }
  };

  const orgCodes = await readCodes(orgScoped);
  if (orgCodes.length > 0) return orgCodes;

  const merged = new Set<string>();
  for (const pr of active) {
    for (const slug of await readCodes(pr)) merged.add(slug);
  }
  return [...merged];
}

type StaffAccountSnapshot = {
  status: UserType['status'];
  role: Role;
  heldRoles: Role[];
};

function deriveStaffAccountFromPractitioner(
  practitioner: Practitioner,
  roleSlugs: string[],
  fallbackRole?: Role,
): StaffAccountSnapshot {
  if (practitioner.active === false) {
    return {
      status: 'Inactive',
      role: (fallbackRole ?? getRole(practitioner) ?? 'provider') as Role,
      heldRoles: [],
    };
  }

  const mergedHeld = normalizeHeldRoles([
    ...resolvePermissionsFromProfile(practitioner).availableRoles,
    ...roleSlugs,
  ]) as Role[];
  const heldRoles = mergedHeld.length > 0
    ? mergedHeld
    : fallbackRole
      ? (normalizeHeldRoles([fallbackRole]) as Role[])
      : [];
  const loginDefault = getRole(practitioner);
  const role = (derivePrimaryRole(heldRoles) ?? loginDefault ?? fallbackRole ?? 'provider') as Role;
  return { status: 'Active', role, heldRoles };
}

async function loadPractitionerRoleSlugs(
  medplum: ReturnType<typeof useMedplum>,
  practitioner: Practitioner,
): Promise<string[]> {
  const orgRefFromPractitioner = practitioner.extension?.find(
    (e) => e.url === ADMIN_EXTENSION_URLS.organization,
  )?.valueReference?.reference;

  try {
    const practitionerRoles = await medplum.searchResources('PractitionerRole', {
      practitioner: `Practitioner/${practitioner.id}`,
      _count: '20',
    });
    return collectPractitionerRoleSlugs(medplum, practitionerRoles, orgRefFromPractitioner);
  } catch {
    return [];
  }
}

const CREDENTIAL_BILLING_HINTS = {
  license: 'Contact billing to add licenses.',
  certification: 'Contact billing to add certifications.',
  dea: 'Contact billing to add DEA credentials.',
};

const BOARD_CERT_TYPE_OPTIONS = [
  'Board Certified',
  'Board Eligible',
  'Subspecialty',
  'Other',
];

const SPECIALTY_TRAINING_TYPE_OPTIONS = [
  'Fellowship',
  'Residency',
  'Postdoctoral',
  'Continuing Education',
  'Other',
];

function getIdentifierValue(resource: Practitioner, system: string): string {
  return resource.identifier?.find((identifier) => identifier.system === system)?.value ?? '';
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

function parseJsonArray(raw?: string): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [];
  } catch {
    return raw ? [raw] : [];
  }
}

function removeExtension(resource: Practitioner, url: string): Practitioner {
  return {
    ...resource,
    extension: (resource.extension ?? []).filter((extension) => extension.url !== url),
  };
}

function upsertStringExtension(resource: Practitioner, url: string, value: string): Practitioner {
  const extensions = (resource.extension ?? []).filter((extension) => extension.url !== url);
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return { ...resource, extension: extensions };
  }

  return {
    ...resource,
    extension: [...extensions, { url, valueString: trimmedValue } satisfies Extension],
  };
}

function upsertJsonStringExtension(resource: Practitioner, url: string, values: string[]): Practitioner {
  const extensions = (resource.extension ?? []).filter((extension) => extension.url !== url);
  if (values.length === 0) {
    return { ...resource, extension: extensions };
  }

  return {
    ...resource,
    extension: [...extensions, { url, valueString: JSON.stringify(values) } satisfies Extension],
  };
}

function upsertTelecom(resource: Practitioner, point: ContactPoint): Practitioner {
  const telecom = (resource.telecom ?? []).filter(
    (entry) => !(entry.system === point.system && entry.use === point.use),
  );

  if (!point.value?.trim()) {
    return { ...resource, telecom };
  }

  return {
    ...resource,
    telecom: [...telecom, { ...point, value: point.value.trim() }],
  };
}

function buildHeadshotAttachment(file: File, attachment: Attachment): Attachment {
  return {
    ...attachment,
    title: attachment.title ?? file.name,
    contentType: attachment.contentType ?? file.type,
  };
}

function getQualificationCode(qualification: Qualification): string | undefined {
  return qualification.code?.coding?.find((coding) => coding.system === CREDENTIAL_TYPE_SYSTEM)?.code;
}

function getQualificationExtension(qualification: Qualification, url: string): Extension | undefined {
  return qualification.extension?.find((extension) => extension.url === url);
}

function getQualificationString(qualification: Qualification, url: string): string | undefined {
  const extension = getQualificationExtension(qualification, url);
  return extension?.valueString ?? extension?.valueCode;
}

function getQualificationBoolean(qualification: Qualification, url: string): boolean | undefined {
  return getQualificationExtension(qualification, url)?.valueBoolean;
}

function getEditableIdentifierValue(qualification: Qualification): string {
  return (
    qualification.identifier?.find(
      (identifier) =>
        identifier.system !== 'https://unify.health/fhir/salesforce-id' &&
        identifier.system !== 'https://unify.health/fhir/verifiable-id',
    )?.value ?? ''
  );
}

function toIsoDate(value?: string): string | undefined {
  if (!value || value === '—') return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return value.trim();
  const parsed = parseDateOnlyLocal(value.trim());
  if (parsed) return formatDateOnlyLocal(parsed);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return toFhirDateOnlyLocal(date);
}

function toIsoDateTime(value?: string): string | undefined {
  if (!value || value === '—') return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

function licenseToQualification(license: License): Qualification {
  return {
    id: license.id,
    code: {
      text: license.licenseName,
      coding: [{ system: CREDENTIAL_TYPE_SYSTEM, code: 'license', display: 'License' }],
    },
    identifier: license.licenseNumber ? [{ value: license.licenseNumber }] : undefined,
    period: {
      start: toIsoDateTime(license.issueDate),
      end: toIsoDate(license.expirationDate),
    },
    extension: [
      ...(license.jurisdictionState
        ? [{ url: QUALIFICATION_EXT_URLS.jurisdictionState, valueCode: license.jurisdictionState } satisfies Extension]
        : []),
      ...(license.licenseStatus
        ? [{ url: QUALIFICATION_EXT_URLS.licenseStatus, valueCode: license.licenseStatus } satisfies Extension]
        : []),
      ...(license.licenseType
        ? [{ url: QUALIFICATION_EXT_URLS.licenseType, valueString: license.licenseType } satisfies Extension]
        : []),
      ...(license.primaryLicense
        ? [{ url: QUALIFICATION_EXT_URLS.isPrimary, valueBoolean: true } satisfies Extension]
        : []),
      ...(license.superseded
        ? [{ url: QUALIFICATION_EXT_URLS.superseded, valueBoolean: true } satisfies Extension]
        : []),
    ],
  };
}

function certificationToQualification(certification: Certification): Qualification {
  return {
    id: certification.id,
    code: {
      text: certification.boardCertificationName,
      coding: [{ system: CREDENTIAL_TYPE_SYSTEM, code: 'board-certification', display: 'Board Certification' }],
    },
    identifier: certification.certificationNumber ? [{ value: certification.certificationNumber }] : undefined,
    period: {
      start: toIsoDate(certification.certificationDate),
    },
    extension: [
      ...(certification.certType
        ? [{ url: QUALIFICATION_EXT_URLS.boardCertificationType, valueCode: certification.certType } satisfies Extension]
        : []),
    ],
  };
}

function deaToQualification(dea: DeaCredential): Qualification {
  return {
    id: dea.id,
    code: {
      text: dea.deaNumberName,
      coding: [{ system: CREDENTIAL_TYPE_SYSTEM, code: 'dea', display: 'DEA' }],
    },
    period: {
      end: toIsoDate(dea.expirationDate),
    },
    extension: [
      { url: QUALIFICATION_EXT_URLS.licenseStatus, valueCode: dea.active ? 'active' : 'inactive' } satisfies Extension,
      ...(dea.drugSchedules
        ? [{ url: QUALIFICATION_EXT_URLS.drugSchedules, valueString: dea.drugSchedules } satisfies Extension]
        : []),
      ...(dea.businessActivity
        ? [{ url: QUALIFICATION_EXT_URLS.businessActivity, valueString: dea.businessActivity } satisfies Extension]
        : []),
    ],
  };
}

function supplementalToQualification(credential: SupplementalCredential): Qualification {
  return {
    id: credential.id,
    code: {
      text: credential.trainingName,
      coding: [{ system: CREDENTIAL_TYPE_SYSTEM, code: 'supplemental', display: 'Supplemental' }],
    },
    period: {
      start: toIsoDate(credential.completedDate),
    },
    extension: [
      ...(credential.trainingType
        ? [{ url: QUALIFICATION_EXT_URLS.trainingType, valueCode: credential.trainingType } satisfies Extension]
        : []),
    ],
  };
}

export function getGroupTypeIcon(type: ClientGroup['type']) {
  switch (type) {
    case 'Couples Therapy':
      return <IconHeart size={16} />;
    case 'Family Therapy':
      return <IconHome size={16} />;
    case 'Group Therapy':
    case 'Support Group':
      return <IconUsersGroup size={16} />;
    default:
      return <IconUsers size={16} />;
  }
}

export function getGroupTypeColor(type: ClientGroup['type']): string {
  switch (type) {
    case 'Couples Therapy':
      return 'pink';
    case 'Family Therapy':
      return 'teal';
    case 'Group Therapy':
      return 'violet';
    case 'Support Group':
      return 'blue';
    default:
      return 'gray';
  }
}

/** Scroll the nearest scroll container (settings pane or window) to the profile header. */
function scrollUserProfileToTop(anchor: HTMLElement | null): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      anchor?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  });
}

function profilePhotoUrl(photo: Attachment | undefined): string | undefined {
  return photo?.data && photo.contentType ? attachmentDataUrl(photo) : photo?.url;
}

export function UserSettings({
  user,
  onBack: _onBack,
  onUpdateUser,
  autoSave = false,
  isPlatformView: _isPlatformView,
  allUsers: _allUsers,
  practices: _practices,
  client,
  clientGroups: _clientGroups = [],
  providers: _providers = [],
  mode = 'full',
}: UserSettingsProps) {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { role: sessionRole, availableRoles, permissions: editorPermissions } = usePermissions();
  const editorRole = getAssignmentActorRole(availableRoles, sessionRole);
  const [editedUser, setEditedUser] = useState(user);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('profile');
  const [editedClient, setEditedClient] = useState<Client | null>(client ?? null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const avatarFileInputRef = useRef<HTMLInputElement>(null);
  /** Draft bio — kept in a ref so typing does not re-render the full UserSettings tree each keystroke. */
  const bioDraftRef = useRef(user.bio ?? '');
  const preferredFirstDraftRef = useRef(user.clinical?.preferredFirstName ?? '');
  const preferredLastDraftRef = useRef(user.clinical?.preferredLastName ?? '');
  const [staffPreferredInputs, setStaffPreferredInputs] = useState({
    first: user.clinical?.preferredFirstName ?? '',
    last: user.clinical?.preferredLastName ?? '',
  });
  const demographicsDraftRef = useRef<DemographicsDraftHandle>(null);
  const dobZipDraftRef = useRef<DobZipDraftHandle>(null);
  const [autosaveStatus, setAutosaveStatus] = useState<SaveStatus>('saved');
  const autosaveRevisionRef = useRef(0);
  const saveHandlerRef = useRef<(options?: { autosave?: boolean; forcePersist?: boolean }) => Promise<boolean>>(async () => true);
  const autosaveControllerRef = useRef<ProfileAutosaveController | null>(null);
  if (!autosaveControllerRef.current) {
    autosaveControllerRef.current = createProfileAutosaveController(
      async (forcePersist) => {
        const submittedRevision = autosaveRevisionRef.current;
        setAutosaveStatus('saving');
        const saved = await saveHandlerRef.current({ autosave: true, forcePersist });
        setAutosaveStatus((current) => statusAfterAutosave(
          saved,
          submittedRevision,
          autosaveRevisionRef.current,
          current,
        ));
        return saved;
      },
    );
  }

  const scheduleAutosave = useCallback(() => {
    if (!autoSave) return;
    autosaveRevisionRef.current += 1;
    setAutosaveStatus('unsaved');
    autosaveControllerRef.current?.schedule();
  }, [autoSave]);

  const retryAutosave = useCallback(() => {
    setAutosaveStatus('saving');
    void autosaveControllerRef.current?.flush();
  }, []);

  useEffect(() => {
    return () => {
      void autosaveControllerRef.current?.flush();
    };
  }, []);

  const markDraftDirty = useCallback(() => {
    setHasChanges(true);
    scheduleAutosave();
  }, [scheduleAutosave]);
  const profileHydratedVersionRef = useRef<string | undefined>(undefined);

  const profileForm = useForm<ProviderSettingsProfileTabFormValues>({
    initialValues: EMPTY_PROVIDER_PROFILE_TAB,
    validate: zod4Resolver(providerSettingsProfileTabSchema),
  });

  const clinicalForm = useForm<ProviderSettingsClinicalFormValues>({
    initialValues: EMPTY_PROVIDER_CLINICAL,
    validate: zod4Resolver(providerSettingsClinicalSchema),
  });

  const educationModalForm = useForm<EducationModalFormValues>({
    initialValues: EMPTY_EDU_MODAL,
    validate: zod4Resolver(educationModalSchema),
  });

  const licenseModalForm = useForm<LicenseModalFormValues>({
    initialValues: EMPTY_LICENSE_MODAL,
    validate: zod4Resolver(licenseModalSchema),
  });

  const boardCertModalForm = useForm<BoardCertModalFormValues>({
    initialValues: EMPTY_CERT_MODAL,
    validate: zod4Resolver(boardCertModalSchema),
  });

  const deaModalForm = useForm<DeaModalFormValues>({
    initialValues: EMPTY_DEA_MODAL,
    validate: zod4Resolver(deaModalSchema),
  });

  const supplementalModalForm = useForm<SupplementalModalFormValues>({
    initialValues: EMPTY_SUPP_MODAL,
    validate: zod4Resolver(supplementalModalSchema),
  });

  const trainingModalForm = useForm<TrainingModalFormValues>({
    initialValues: EMPTY_TRAINING_MODAL,
    validate: zod4Resolver(trainingModalSchema),
  });
  const [credentialing, setCredentialing] = useState<InsuranceCredential[]>(
    user.credentialing || []
  );
  const [providerPractitioner, setProviderPractitioner] = useState<Practitioner | null>(null);
  const [practitionerProfileLoadFailed, setPractitionerProfileLoadFailed] = useState(false);
  const providerPractitionerRef = useRef<Practitioner | null>(null);
  useEffect(() => {
    providerPractitionerRef.current = providerPractitioner;
  }, [providerPractitioner]);
  useEffect(() => {
    if (autoSave && providerPractitioner && (avatarFile || hasChanges)) {
      scheduleAutosave();
    }
  }, [autoSave, providerPractitioner, avatarFile, hasChanges, scheduleAutosave]);
  /** Role codes from org-scoped `PractitionerRole.code` (e.g. provider + supervisor). */
  const [, setPractitionerOrgRoleSlugs] = useState<string[]>([]);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [licenseSaving, setLicenseSaving] = useState(false);
  const [showAddLicenseModal, setShowAddLicenseModal] = useState(false);
  const [editingLicenseId, setEditingLicenseId] = useState<string | null>(null);
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [certificationSaving, setCertificationSaving] = useState(false);
  const [showAddCertificationModal, setShowAddCertificationModal] = useState(false);
  const [editingCertificationId, setEditingCertificationId] = useState<string | null>(null);
  const certificationFileInputRef = useRef<HTMLInputElement>(null);
  const [educationEntries, setEducationEntries] = useState<Education[]>([]);
  const [eduModalOpen, setEduModalOpen] = useState(false);
  const [editingEduId, setEditingEduId] = useState<string | null>(null);
  const [deaCredentials, setDeaCredentials] = useState<DeaCredential[]>([]);
  const [deaSaving, setDeaSaving] = useState(false);
  const [deaModalOpen, setDeaModalOpen] = useState(false);
  const [editingDeaId, setEditingDeaId] = useState<string | null>(null);
  const [supplementalCredentials, setSupplementalCredentials] = useState<SupplementalCredential[]>([]);
  const [supplementalSaving, setSupplementalSaving] = useState(false);
  const [suppModalOpen, setSuppModalOpen] = useState(false);
  const [editingSuppId, setEditingSuppId] = useState<string | null>(null);
  const [caqhSaving, setCaqhSaving] = useState(false);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [trainingModalOpen, setTrainingModalOpen] = useState(false);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>(
    user.locationIds || []
  );
  const persistedLocationIdsRef = useRef<string[]>(user.locationIds ?? []);
  useEffect(() => {
    persistedLocationIdsRef.current = user.locationIds ?? [];
  }, [user.id]);
  const [editingCredential, setEditingCredential] =
    useState<InsuranceCredential | null>(null);
  const [isAddingCredential, setIsAddingCredential] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [credentialToDelete, setCredentialToDelete] = useState<string | null>(
    null
  );
  const isSelf = profile?.id === user.id;
  const [selfLastActive, setSelfLastActive] = useState<string | null>(null);
  const [joinedAt, setJoinedAt] = useState<string | null>(null);
  useEffect(() => {
    if (!isSelf) {
      setSelfLastActive(null);
      return;
    }
    let cancelled = false;
    setSelfLastActive(new Date().toISOString());
    void fetchAuthMeSnapshot(medplum)
      .then((snapshot) => {
        if (cancelled) return;
        setSelfLastActive(latestSessionLastSeen(snapshot.sessions) ?? new Date().toISOString());
      })
      .catch(() => {
        if (cancelled) return;
        setSelfLastActive(new Date().toISOString());
      });
    return () => {
      cancelled = true;
    };
  }, [isSelf, medplum, user.id]);
  useEffect(() => {
    let cancelled = false;
    setJoinedAt(editedUser.createdAt || null);
    if (user.role === 'client') return;
    void fetchCreatedAtFromHistory(medplum, 'Practitioner', user.id)
      .then((createdAt) => {
        if (cancelled) return;
        setJoinedAt(createdAt ?? editedUser.createdAt ?? null);
      })
      .catch(() => {
        if (!cancelled) setJoinedAt(editedUser.createdAt || null);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, user.id, user.role, editedUser.createdAt]);
  const canEditUserRole =
    editorPermissions.edit_users === true
    && isUserRoleFieldEditable(editorRole, user.role, isSelf);
  const showRolePermissionsReference = canViewRolePermissionsReference(editorRole);
  const [heldRoles, setHeldRoles] = useState<Role[]>(() =>
    normalizeHeldRoles(heldRolesForUser(user)) as Role[],
  );
  const [baselineHeldRoles, setBaselineHeldRoles] = useState<Role[]>(() =>
    normalizeHeldRoles(heldRolesForUser(user)) as Role[],
  );
  const assignableRoles = useMemo(() => {
    const roles = getAssignableRolesForHeldRoles(availableRoles, derivePrimaryRole(heldRoles) ?? user.role);
    if (isUnifyPlatformStaffRole(editorRole)) return roles;
    return roles.filter((r) => !rolesNeedZoomSeat([r]));
  }, [availableRoles, heldRoles, user.role, editorRole]);
  const roleSetDirty = !sameRoleSet(heldRoles, baselineHeldRoles);
  const derivedPrimary = (derivePrimaryRole(heldRoles) ?? user.role) as Role;
  const baselinePrimary = (derivePrimaryRole(baselineHeldRoles) ?? user.role) as Role;
  const [roleTransitionOpen, setRoleTransitionOpen] = useState(false);
  const [roleTransitionMode, setRoleTransitionMode] = useState<'role-change' | 'offboard' | 'reactivate'>('role-change');
  const [pendingHeldRoles, setPendingHeldRoles] = useState<Role[] | null>(null);
  const roleTransitionCommittedRef = useRef(false);
  const pageTopRef = useRef<HTMLDivElement>(null);
  const [roleSaving, setRoleSaving] = useState(false);
  const [defaultRoleSaving, setDefaultRoleSaving] = useState(false);
  /** Bumped after a role write so supervision, assignment history, and the profile re-read. */
  const [roleRefreshToken, setRoleRefreshToken] = useState(0);

  const handleLocationsInit = useCallback((ids: string[]) => {
    setSelectedLocationIds(ids);
    persistedLocationIdsRef.current = [...ids];
  }, []);

  const handleLocationToggle = useCallback(
    (id: string, checked: boolean) => {
      const previous = selectedLocationIds;
      const next = checked
        ? [...new Set([...previous, id])]
        : previous.filter((locationId) => locationId !== id);
      setSelectedLocationIds(next);
      void (async () => {
        try {
          await Promise.resolve(onUpdateUser(user.id, { locationIds: next }));
          persistedLocationIdsRef.current = [...next];
        } catch (error) {
          setSelectedLocationIds(previous);
          notifications.show({
            title: 'Could not update locations',
            message: error instanceof Error ? error.message : 'Please try again.',
            color: 'red',
          });
        }
      })();
    },
    [onUpdateUser, selectedLocationIds, user.id],
  );

  const isProvider =
    holdsTreatingCapability(heldRoles) || holdsSupervisingCapability(heldRoles);
  const hideProviderClinicalTabs = isUnifyAdminShellRole(sessionRole);
  const showClinicalInfoTab =
    isProvider && FEATURE_VISIBILITY.settingsClinicalInfo && !hideProviderClinicalTabs;
  const showAppointmentTypesTab =
    isProvider && FEATURE_VISIBILITY.settingsAppointmentTypes && !hideProviderClinicalTabs;
  const locationsOrgId =
    editedUser.practiceId
    || providerPractitioner?.extension
      ?.find((e) => e.url === ADMIN_EXTENSION_URLS.organization)
      ?.valueReference?.reference?.replace(/^Organization\//, '')
    || '';

  const persistQualificationsOfType = useCallback(
    async (credentialCode: string, nextQualifications: Qualification[]) => {
      if (!providerPractitioner?.id) {
        throw new Error('Practitioner profile is not loaded.');
      }
      const updated: Practitioner = {
        ...providerPractitioner,
        qualification: [
          ...(providerPractitioner.qualification ?? []).filter(
            (qualification) => getQualificationCode(qualification) !== credentialCode,
          ),
          ...nextQualifications,
        ],
      };
      const saved = await medplum.updateResource(updated);
      providerPractitionerRef.current = saved;
      setProviderPractitioner(saved);
      medplum.invalidateSearches('Practitioner');
    },
    [medplum, providerPractitioner],
  );

  const persistLicensesToPractitioner = useCallback(
    async (nextLicenses: License[]) => {
      await persistQualificationsOfType('license', nextLicenses.map(licenseToQualification));
    },
    [persistQualificationsOfType],
  );

  const persistCertificationsToPractitioner = useCallback(
    async (nextCertifications: Certification[]) => {
      await persistQualificationsOfType(
        'board-certification',
        nextCertifications.map(certificationToQualification),
      );
    },
    [persistQualificationsOfType],
  );

  const persistDeaToPractitioner = useCallback(
    async (nextDea: DeaCredential[]) => {
      await persistQualificationsOfType('dea', nextDea.map(deaToQualification));
    },
    [persistQualificationsOfType],
  );

  const persistSupplementalToPractitioner = useCallback(
    async (nextSupplemental: SupplementalCredential[]) => {
      await persistQualificationsOfType(
        'supplemental',
        nextSupplemental.map(supplementalToQualification),
      );
    },
    [persistQualificationsOfType],
  );

  const saveCaqhInformation = clinicalForm.onSubmit((clinicalValues) => {
    if (!providerPractitioner?.id) {
      notifications.show({
        title: 'Cannot save CAQH information',
        message: 'Practitioner profile is still loading. Try again in a moment.',
        color: 'red',
      });
      return;
    }
    setCaqhSaving(true);
    void (async () => {
      try {
        let updated: Practitioner = { ...providerPractitioner };
        updated = clinicalValues.caqhId.trim()
          ? setCaqhIdExtension(updated, clinicalValues.caqhId.trim())
          : removeExtension(updated, CAQH_EXT_URLS.id);
        updated = clinicalValues.caqhUsername.trim()
          ? setCaqhUsernameExtension(updated, clinicalValues.caqhUsername.trim())
          : removeExtension(updated, CAQH_EXT_URLS.username);
        updated = clinicalValues.caqhPassword.trim()
          ? setCaqhPasswordExtension(updated, clinicalValues.caqhPassword.trim())
          : removeExtension(updated, CAQH_EXT_URLS.password);
        updated = clinicalValues.lastCaqhAttestation
          ? setCaqhAttestationDateExtension(updated, clinicalValues.lastCaqhAttestation)
          : removeExtension(updated, CAQH_EXT_URLS.attestationDate);

        const saved = await medplum.updateResource(updated);
        providerPractitionerRef.current = saved;
        setProviderPractitioner(saved);
        medplum.invalidateSearches('Practitioner');
        clinicalForm.resetDirty();
        notifications.show({
          title: 'CAQH saved',
          message: 'CAQH information was saved.',
          color: 'green',
        });
      } catch (error) {
        notifications.show({
          title: 'Could not save CAQH information',
          message: error instanceof Error ? error.message : 'Please try again.',
          color: 'red',
        });
      } finally {
        setCaqhSaving(false);
      }
    })();
  });

  const persistHeadshot = useCallback(
    async (file: File) => {
      if (!providerPractitioner?.id) {
        throw new Error('Practitioner profile is not loaded.');
      }
      const headshotResult = providerSettingsHeadshotSchema.safeParse({
        avatarFile: file,
        hasExistingHeadshot: Boolean(providerPractitioner.photo?.[0]),
      });
      if (!headshotResult.success) {
        throw new Error(
          missingFieldMessage(
            headshotResult.error.issues.map((issue) => String(issue.path[0] ?? '')),
            providerSettingsHeadshotFieldLabels,
          ),
        );
      }
      const uploadedHeadshot = await createSecuredAttachment(
        medplum,
        file,
        { reference: `Practitioner/${providerPractitioner.id}` },
        file.name,
        file.type,
      );
      const updated: Practitioner = {
        ...providerPractitioner,
        photo: [buildHeadshotAttachment(file, uploadedHeadshot)],
      };
      const saved = await medplum.updateResource(updated);
      providerPractitionerRef.current = saved;
      setProviderPractitioner(saved);
      medplum.invalidateSearches('Practitioner');
      const savedPhotoUrl = profilePhotoUrl(saved.photo?.[0]);
      if (savedPhotoUrl) {
        setAvatarUrl(savedPhotoUrl);
      }
      setAvatarFile(null);
    },
    [medplum, providerPractitioner],
  );

  const persistRoleSet = async (next: Role[]): Promise<boolean> => {
    if (sameRoleSet(next, baselineHeldRoles)) return true;
    if (!canEditUserRole) {
      notifications.show({
        title: 'Cannot change roles',
        message: 'You do not have permission to manage roles for this user.',
        color: 'red',
      });
      setHeldRoles(baselineHeldRoles);
      return false;
    }
    const primary = derivePrimaryRole(next);
    if (!primary) {
      // Empty set is not a role transition — deactivate instead.
      setHeldRoles(baselineHeldRoles);
      notifications.show({
        title: 'Cannot remove last role',
        message: 'Deactivate the user instead of removing their last role.',
        color: 'orange',
      });
      return false;
    }
    if (!canEditorAssignRole(editorRole, primary)) {
      notifications.show({
        title: 'Cannot change roles',
        message: 'You do not have permission to assign the resulting primary role.',
        color: 'red',
      });
      setHeldRoles(baselineHeldRoles);
      return false;
    }
    if (roleSetChangeNeedsTransitionWizard(baselineHeldRoles, next)) {
      setPendingHeldRoles(next);
      setRoleTransitionMode('role-change');
      setRoleTransitionOpen(true);
      return false;
    }
    setRoleSaving(true);
    try {
      const additional = next.filter((r) => r !== primary);
      await setRole(medplum, user.id, primary, undefined, additional);
      await Promise.resolve(onUpdateUser(user.id, { role: primary, heldRoles: next }));
      setEditedUser((prev) => ({ ...prev, role: primary }));
      setHeldRoles(next);
      setBaselineHeldRoles(next);
      setPractitionerOrgRoleSlugs(next);
      setRoleRefreshToken((n) => n + 1);
      notifications.show({
        title: 'Roles updated',
        message: `${user.name} now holds ${formatCollapsedRoleLabels(next)}.`,
        color: 'green',
      });
      return true;
    } catch (error) {
      setHeldRoles(baselineHeldRoles);
      notifications.show({
        title: 'Role update failed',
        message: error instanceof Error ? error.message : 'Could not update roles.',
        color: 'red',
      });
      return false;
    } finally {
      setRoleSaving(false);
    }
  };

  const handleDefaultRoleChange = async (slug: string | null) => {
    if (!isSelf || !slug || !heldRoles.includes(slug as Role)) return;
    if (slug === editedUser.role) return;
    setDefaultRoleSaving(true);
    try {
      const saved = await setPrimaryRole(medplum, user.id, slug);
      setProviderPractitioner(saved);
      // set-primary-role already persisted; skip PractitionerRole refresh.
      await Promise.resolve(onUpdateUser(user.id, { role: slug as Role, heldRoles }));
      setEditedUser((prev) => ({ ...prev, role: slug as Role }));
      notifications.show({
        title: 'Default role updated',
        message: 'Shown when you sign in. Your current session role is unchanged.',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Could not update default role',
        message: error instanceof Error ? error.message : 'Please try again.',
        color: 'red',
      });
    } finally {
      setDefaultRoleSaving(false);
    }
  };

  const handleAddCredential = (
    credential: Omit<InsuranceCredential, 'id'>
  ) => {
    const newCredential: InsuranceCredential = {
      ...credential,
      id: Date.now().toString(),
    };
    setCredentialing([...credentialing, newCredential]);
    setIsAddingCredential(false);
    setHasChanges(true);
    scheduleAutosave();
  };

  const handleUpdateCredential = (
    id: string,
    updates: Partial<InsuranceCredential>
  ) => {
    setCredentialing(
      credentialing.map((c) => (c.id === id ? { ...c, ...updates } : c))
    );
    setEditingCredential(null);
    setHasChanges(true);
    scheduleAutosave();
  };

  const closeAddLicenseModal = () => {
    setShowAddLicenseModal(false);
    setEditingLicenseId(null);
    licenseModalForm.setValues(EMPTY_LICENSE_MODAL);
  };

  const parseDisplayDateToIso = (displayStr: string | undefined): string | null => {
    if (!displayStr || displayStr === '—') return null;
    return toIsoDate(displayStr) ?? null;
  };

  const closeAddCertificationModal = () => {
    setShowAddCertificationModal(false);
    setEditingCertificationId(null);
    boardCertModalForm.setValues(EMPTY_CERT_MODAL);
    if (certificationFileInputRef.current) certificationFileInputRef.current.value = '';
  };

  const formatDateForDisplay = (dateStr: string | null) => {
    if (!dateStr) return '';
    const d = calendarDateFromPickerValue(dateStr);
    if (!d) return dateStr;
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  };

  const toIsoFromDateInput = (value: Date | string | null): string | null => {
    if (!value) return null;
    const normalized = dateInputValueToDateOnly(value);
    return normalized || null;
  };

  const openEditCertification = (cert: Certification) => {
    setEditingCertificationId(cert.id);
    const parsed = cert.certificationDate && cert.certificationDate !== '—'
      ? parseDisplayDateToIso(cert.certificationDate)
      : null;
    boardCertModalForm.setValues({
      boardCertificationName: cert.boardCertificationName,
      certificationNumber: cert.certificationNumber,
      certificationDate: parsed,
      certType: cert.certType ?? null,
      certificateFile: null,
    });
    setShowAddCertificationModal(true);
  };

  const saveCertification = boardCertModalForm.onSubmit((values) => {
    if (!providerPractitioner?.id) {
      notifications.show({
        title: 'Cannot save certification',
        message: 'Practitioner profile is still loading. Try again in a moment.',
        color: 'red',
      });
      return;
    }
    const entry: Certification = {
      id: editingCertificationId ?? String(Date.now()),
      boardCertificationName: values.boardCertificationName.trim(),
      certificationNumber: values.certificationNumber,
      certificationDate: formatDateForDisplay(values.certificationDate) || '—',
      certificateFileName: values.certificateFile?.name ?? '',
      certType: values.certType ?? undefined,
    };
    const previousCertifications = certifications;
    const wasEdit = Boolean(editingCertificationId);
    const nextCertifications = editingCertificationId
      ? certifications.map((c) =>
          c.id === editingCertificationId
            ? {
                ...entry,
                certificateFileName: values.certificateFile?.name ?? c.certificateFileName,
              }
            : c,
        )
      : [...certifications, entry];
    setCertifications(nextCertifications);
    closeAddCertificationModal();
    setCertificationSaving(true);
    void (async () => {
      try {
        await persistCertificationsToPractitioner(nextCertifications);
        notifications.show({
          title: 'Certification saved',
          message: wasEdit ? 'Certification changes were saved.' : 'Certification was added.',
          color: 'green',
        });
      } catch (error) {
        setCertifications(previousCertifications);
        notifications.show({
          title: 'Could not save certification',
          message: error instanceof Error ? error.message : 'Please try again.',
          color: 'red',
        });
      } finally {
        setCertificationSaving(false);
      }
    })();
  });

  const openEditLicense = (license: License) => {
    setEditingLicenseId(license.id);
    licenseModalForm.setValues({
      licenseName: license.licenseName,
      licenseNumber: license.licenseNumber,
      jurisdictionState: license.jurisdictionState || null,
      licenseStatus: license.licenseStatus || '',
      issueDate: parseDisplayDateToIso(license.issueDate),
      expirationDate: parseDisplayDateToIso(license.expirationDate === '—' ? undefined : license.expirationDate),
      licenseType: license.licenseType ?? null,
      isPrimary: license.primaryLicense ?? false,
      superseded: license.superseded ?? false,
      documentFile: null,
    });
    setShowAddLicenseModal(true);
  };

  const saveLicense = licenseModalForm.onSubmit((values) => {
    if (!providerPractitioner?.id) {
      notifications.show({
        title: 'Cannot save license',
        message: 'Practitioner profile is still loading. Try again in a moment.',
        color: 'red',
      });
      return;
    }
    const now = new Date();
    const lastModifiedStr = `${now.getMonth() + 1}/${now.getDate()}/${now.getFullYear()}, ${now.getHours() % 12 || 12}:${String(now.getMinutes()).padStart(2, '0')} ${now.getHours() >= 12 ? 'PM' : 'AM'}`;
    const entry: License = {
      id: editingLicenseId ?? String(Date.now()),
      licenseName: values.licenseName.trim(),
      licenseNumber: values.licenseNumber,
      jurisdictionState: values.jurisdictionState ?? '',
      licenseStatus: values.licenseStatus || 'Active',
      expirationDate: formatDateForDisplay(values.expirationDate) || '—',
      primaryLicense: values.isPrimary,
      lastModifiedDate: lastModifiedStr,
      issueDate: formatDateForDisplay(values.issueDate) || undefined,
      licenseType: values.licenseType ?? undefined,
      superseded: values.superseded,
    };
    const previousLicenses = licenses;
    const wasEdit = Boolean(editingLicenseId);
    const nextLicenses = editingLicenseId
      ? licenses.map((lic) => (lic.id === editingLicenseId ? entry : lic))
      : [...licenses, entry];
    setLicenses(nextLicenses);
    closeAddLicenseModal();
    setLicenseSaving(true);
    void (async () => {
      try {
        await persistLicensesToPractitioner(nextLicenses);
        notifications.show({
          title: 'License saved',
          message: wasEdit ? 'License changes were saved.' : 'License was added.',
          color: 'green',
        });
      } catch (error) {
        setLicenses(previousLicenses);
        notifications.show({
          title: 'Could not save license',
          message: error instanceof Error ? error.message : 'Please try again.',
          color: 'red',
        });
      } finally {
        setLicenseSaving(false);
      }
    })();
  });

  const closeEducationModal = () => {
    setEduModalOpen(false);
    setEditingEduId(null);
    educationModalForm.setValues(EMPTY_EDU_MODAL);
  };

  const openAddEducation = () => {
    setEditingEduId(null);
    educationModalForm.setValues(EMPTY_EDU_MODAL);
    setEduModalOpen(true);
  };

  const openEditEducation = (education: Education) => {
    setEditingEduId(education.id);
    educationModalForm.setValues({
      institution: education.institution,
      degree: education.degree,
      graduationYear: education.graduationYear,
    });
    setEduModalOpen(true);
  };

  const saveEducation = educationModalForm.onSubmit((values) => {
    const entry: Education = {
      id: editingEduId ?? String(Date.now()),
      institution: values.institution.trim(),
      degree: values.degree.trim(),
      graduationYear: values.graduationYear.trim(),
    };
    if (editingEduId) {
      setEducationEntries((prev) => prev.map((e) => (e.id === editingEduId ? entry : e)));
    } else {
      setEducationEntries((prev) => [...prev, entry]);
    }
    closeEducationModal();
  });

  const closeDeaModal = () => {
    setDeaModalOpen(false);
    setEditingDeaId(null);
    deaModalForm.setValues(EMPTY_DEA_MODAL);
  };

  const openEditDea = (credential: DeaCredential) => {
    setEditingDeaId(credential.id);
    deaModalForm.setValues({
      deaNumberName: credential.deaNumberName,
      active: credential.active,
      expirationDate: parseDisplayDateToIso(credential.expirationDate),
      drugSchedules: credential.drugSchedules,
      businessActivity: credential.businessActivity,
    });
    setDeaModalOpen(true);
  };

  const saveDea = deaModalForm.onSubmit((values) => {
    if (!providerPractitioner?.id) {
      notifications.show({
        title: 'Cannot save DEA credential',
        message: 'Practitioner profile is still loading. Try again in a moment.',
        color: 'red',
      });
      return;
    }
    const nextValue: DeaCredential = {
      id: editingDeaId ?? String(Date.now()),
      deaNumberName: values.deaNumberName.trim(),
      active: values.active,
      expirationDate: formatDateForDisplay(values.expirationDate) || '—',
      drugSchedules: values.drugSchedules,
      businessActivity: values.businessActivity,
    };
    const previousDea = deaCredentials;
    const wasEdit = Boolean(editingDeaId);
    const nextDea = editingDeaId
      ? deaCredentials.map((entry) => (entry.id === editingDeaId ? nextValue : entry))
      : [...deaCredentials, nextValue];
    setDeaCredentials(nextDea);
    closeDeaModal();
    setDeaSaving(true);
    void (async () => {
      try {
        await persistDeaToPractitioner(nextDea);
        notifications.show({
          title: 'DEA credential saved',
          message: wasEdit ? 'DEA credential changes were saved.' : 'DEA credential was added.',
          color: 'green',
        });
      } catch (error) {
        setDeaCredentials(previousDea);
        notifications.show({
          title: 'Could not save DEA credential',
          message: error instanceof Error ? error.message : 'Please try again.',
          color: 'red',
        });
      } finally {
        setDeaSaving(false);
      }
    })();
  });

  const closeSupplementalModal = () => {
    setSuppModalOpen(false);
    setEditingSuppId(null);
    supplementalModalForm.setValues(EMPTY_SUPP_MODAL);
  };

  const openAddSupplemental = () => {
    setEditingSuppId(null);
    supplementalModalForm.setValues(EMPTY_SUPP_MODAL);
    setSuppModalOpen(true);
  };

  const openEditSupplemental = (credential: SupplementalCredential) => {
    setEditingSuppId(credential.id);
    supplementalModalForm.setValues({
      trainingName: credential.trainingName,
      trainingType: credential.trainingType || null,
      completedDate: parseDisplayDateToIso(credential.completedDate),
    });
    setSuppModalOpen(true);
  };

  const saveSupplemental = supplementalModalForm.onSubmit((values) => {
    if (!providerPractitioner?.id) {
      notifications.show({
        title: 'Cannot save specialty training',
        message: 'Practitioner profile is still loading. Try again in a moment.',
        color: 'red',
      });
      return;
    }
    const nextValue: SupplementalCredential = {
      id: editingSuppId ?? String(Date.now()),
      trainingName: values.trainingName.trim(),
      trainingType: values.trainingType ?? '',
      completedDate: formatDateForDisplay(values.completedDate) || '—',
    };
    const previousSupplemental = supplementalCredentials;
    const wasEdit = Boolean(editingSuppId);
    const nextSupplemental = editingSuppId
      ? supplementalCredentials.map((entry) => (entry.id === editingSuppId ? nextValue : entry))
      : [...supplementalCredentials, nextValue];
    setSupplementalCredentials(nextSupplemental);
    closeSupplementalModal();
    setSupplementalSaving(true);
    void (async () => {
      try {
        await persistSupplementalToPractitioner(nextSupplemental);
        notifications.show({
          title: 'Specialty training saved',
          message: wasEdit ? 'Specialty training changes were saved.' : 'Specialty training was added.',
          color: 'green',
        });
      } catch (error) {
        setSupplementalCredentials(previousSupplemental);
        notifications.show({
          title: 'Could not save specialty training',
          message: error instanceof Error ? error.message : 'Please try again.',
          color: 'red',
        });
      } finally {
        setSupplementalSaving(false);
      }
    })();
  });

  const closeTrainingModal = () => {
    setTrainingModalOpen(false);
    trainingModalForm.setValues(EMPTY_TRAINING_MODAL);
  };

  const openAddTraining = () => {
    trainingModalForm.setValues(EMPTY_TRAINING_MODAL);
    setTrainingModalOpen(true);
  };

  const saveTraining = trainingModalForm.onSubmit((values) => {
    setTrainings((prev) => [
      ...prev,
      {
        id: String(Date.now()),
        trainingType: values.trainingType,
        completedDate: formatDateForDisplay(values.completedDate) || '—',
      },
    ]);
    closeTrainingModal();
  });

  const handleSave = async (options: { autosave?: boolean; forcePersist?: boolean } = {}): Promise<boolean> => {
    const { autosave = false, forcePersist = false } = options;
    const submittedRevision = autosaveRevisionRef.current;
    if (roleSetDirty) {
      const roleSaved = await persistRoleSet(heldRoles);
      if (!roleSaved) return false;
    }

    const providerProfileDirty = profileForm.isDirty();
    const practitionerToUpdate = providerPractitionerRef.current ?? providerPractitioner;

    if (user.role !== 'client' && !practitionerToUpdate) {
      return false;
    }

    if (isProvider && practitionerToUpdate) {
      if (shouldSkipUnchangedProviderSave(providerProfileDirty, forcePersist)) {
        if (roleSetDirty) {
          setHasChanges(false);
          notifications.show({
            title: 'Saved',
            message: 'Roles were updated.',
            color: 'green',
          });
        }
        return true;
      }

      if (shouldValidateDirtyForm(profileForm.isDirty())) {
        const profileValidation = profileForm.validate();
        if (profileValidation.hasErrors) {
          if (autosave) setAutosaveStatus('invalid');
          if (!autosave) {
            notifications.show({
              title: 'Validation error',
              message: missingFieldMessage(
                Object.keys(profileForm.errors),
                providerSettingsProfileFieldLabels,
              ),
              color: 'red',
            });
          }
          return false;
        }
      }

      const profileValues = profileForm.values;
      try {
        let updated: Practitioner = { ...practitionerToUpdate };

              const preferredFirstName = profileValues.preferredFirstName.trim();
              const preferredLastName = profileValues.preferredLastName.trim();
              updated = upsertPractitionerNickname(updated, {
                first: preferredFirstName,
                last: preferredLastName,
              });

              updated = upsertTelecom(updated, {
                system: 'phone',
                use: 'work',
                value: profileValues.phone,
              });

              updated.address = [
                {
                  use: 'home',
                  line: profileValues.street.trim() ? [profileValues.street.trim()] : undefined,
                  city: profileValues.city.trim() || undefined,
                  state: profileValues.state.trim() || undefined,
                  postalCode: profileValues.zipCode.trim() || undefined,
                },
              ];
              updated.communication = profileValues.languages.length
                ? profileValues.languages.map((language) => ({ text: language }))
                : [];

              updated = profileValues.bio.trim() ? setBio(updated, profileValues.bio.trim()) : removeExtension(updated, BIO_EXT);
              updated = upsertStringExtension(updated, EXT_URLS.pronouns, profileValues.pronouns);
              updated = profileValues.specialties.length || profileValues.modalities.length || profileValues.ageGroups.length
                ? setClinical(updated, {
                  specialties: profileValues.specialties,
                  modalities: profileValues.modalities,
                  ageGroups: profileValues.ageGroups,
                })
                : removeExtension(updated, CLINICAL_EXT);
              updated = upsertStringExtension(updated, GENDER_IDENTITY_EXT, profileValues.genderIdentity);
              updated = upsertStringExtension(updated, LGBTQIA_IDENTITIES_EXT, profileValues.lgbtqiaIdentity);
              updated = upsertJsonStringExtension(updated, RELIGION_SPIRITUALITY_EXT, profileValues.religion);
              updated = upsertJsonStringExtension(updated, RACE_ETHNICITY_EXT, profileValues.raceEthnicity);

              const savedPractitioner = await medplum.updateResource(updated);
              providerPractitionerRef.current = savedPractitioner;
              const savedDisplay = formatPractitionerNameParts(savedPractitioner);

              medplum.invalidateSearches('Practitioner');
              if (shouldApplyAutosaveResult(
                autosave,
                submittedRevision,
                autosaveRevisionRef.current,
              )) {
                setProviderPractitioner(savedPractitioner);
                setEditedUser((prev) => ({
                  ...prev,
                  name: savedDisplay.primary,
                  phone: profileValues.phone || undefined,
                  bio: profileValues.bio || undefined,
                }));
                setAvatarFile(null);
                const savedPhotoUrl = profilePhotoUrl(savedPractitioner.photo?.[0]);
                if (savedPhotoUrl) {
                  setAvatarUrl(savedPhotoUrl);
                }
                profileForm.resetDirty(profileValues);
                setHasChanges(false);
                if (!autosave) {
                  notifications.show({
                    title: 'Saved',
                    message: 'Profile changes were saved.',
                    color: 'green',
                  });
                }
              }
              return true;
            } catch (error) {
              if (autosave) setAutosaveStatus('error');
              notifications.show({
                title: 'Save failed',
                message: error instanceof Error
                  ? `${error.message} Your changes remain unsaved.`
                  : 'Could not save profile changes. Your changes remain unsaved.',
                color: 'red',
              });
              return false;
            }
    }

    if (shouldSkipUnchangedProviderSave(hasChanges || roleSetDirty, forcePersist)) {
      return true;
    }

    const demographicsFromDraft = demographicsDraftRef.current?.getDemographics();
    const isClientSave = user.role === 'client';
    const clinicalSource = { ...(user.clinical ?? {}) };
    const clinicalForSave = isClientSave
      ? clinicalSource
      : (() => {
          const { preferredFirstName: _legacyFirst, preferredLastName: _legacyLast, ...rest } = clinicalSource;
          return rest;
        })();
    const mergedClinical: NonNullable<UserType['clinical']> = {
      ...clinicalForSave,
      dateOfBirth: dobZipDraftRef.current?.getDateOfBirthYmd() || undefined,
      zipCode: dobZipDraftRef.current?.getZip() || undefined,
    };
    if (demographicsFromDraft) {
      mergedClinical.demographics = demographicsFromDraft;
    }

    const { name: _unchangedLegalName, ...editedWithoutName } = editedUser;
    const updates = applyClientLegalNameToUserUpdates(
      {
        ...(isClientSave ? editedUser : editedWithoutName),
        bio: bioDraftRef.current,
        credentialing,
        locationIds: selectedLocationIds,
        clinical: Object.keys(mergedClinical).length ? mergedClinical : undefined,
        ...(!isClientSave
          ? {
              preferredNames: {
                first: preferredFirstDraftRef.current,
                last: preferredLastDraftRef.current,
              },
            }
          : {}),
      },
      {
        isClientSave,
        editedClient,
        fallbackName: editedUser.name,
        fallbackEmail: editedUser.email,
        fallbackPhone: editedUser.phone,
      },
    );
    try {
      await Promise.resolve(onUpdateUser(
        user.id,
        updates,
        {
          silentSuccess: autosave,
          silentError: true,
          throwOnError: true,
          shouldApplyResult: autosave
            ? () => shouldApplyAutosaveResult(
              true,
              submittedRevision,
              autosaveRevisionRef.current,
            )
            : undefined,
        },
      ));
      persistedLocationIdsRef.current = [...selectedLocationIds];
      if (shouldApplyAutosaveResult(
        autosave,
        submittedRevision,
        autosaveRevisionRef.current,
      )) {
        setHasChanges(false);
        // The staff profile persists through onUpdateUser, so mirror the names we
        // just sent onto the local resource — the header reads them from it.
        const savedPreferred = updates.preferredNames;
        if (savedPreferred) {
          setProviderPractitioner((prev) => (
            prev
              ? stripPractitionerLegacyPreferredNames(upsertPractitionerNickname(prev, savedPreferred))
              : prev
          ));
        }
      }
      return true;
    } catch (error) {
      if (autosave) setAutosaveStatus('error');
      notifications.show({
        title: 'Save failed',
        message: error instanceof Error
          ? `${error.message} Your changes remain unsaved.`
          : 'Could not save profile changes. Your changes remain unsaved.',
        color: 'red',
      });
      return false;
    }
  };

  saveHandlerRef.current = handleSave;

  useEffect(() => {
    if (!isProvider && (activeTab === 'clinical' || activeTab === 'appointmentTypes')) {
      setActiveTab('profile');
    }
    if (activeTab === 'clinical' && !showClinicalInfoTab) {
      setActiveTab('profile');
    }
    if (activeTab === 'appointmentTypes' && !showAppointmentTypesTab) {
      setActiveTab('profile');
    }
    if (isSelf && activeTab === 'permissions') {
      setActiveTab('profile');
    }
  }, [isProvider, isSelf, activeTab, showClinicalInfoTab, showAppointmentTypesTab]);

  useEffect(() => {
    setEditedClient(client ?? null);
  }, [client]);

  useEffect(() => {
    setEditedUser(user);
    const nextHeld = normalizeHeldRoles(heldRolesForUser(user)) as Role[];
    setHeldRoles(nextHeld);
    setBaselineHeldRoles(nextHeld);
  }, [user]);

  useEffect(() => {
    bioDraftRef.current = user.bio ?? '';
  }, [user.id, user.bio]);

  const applyStaffAccountSnapshot = useCallback((snapshot: StaffAccountSnapshot) => {
    setHeldRoles(snapshot.heldRoles);
    setBaselineHeldRoles(snapshot.heldRoles);
    setPractitionerOrgRoleSlugs(snapshot.heldRoles);
    setEditedUser((prev) => ({ ...prev, status: snapshot.status, role: snapshot.role }));
  }, []);

  const refreshStaffAccountFromServer = useCallback(
    async (fallbackRole?: Role): Promise<StaffAccountSnapshot> => {
      medplum.invalidateSearches('Practitioner');
      medplum.invalidateSearches('PractitionerRole');
      const practitioner = await medplum.readResource('Practitioner', user.id, PROFILE_RESOURCE_READ_OPTIONS);
      setProviderPractitioner(practitioner);
      const roleSlugs = await loadPractitionerRoleSlugs(medplum, practitioner);
      let snapshot = deriveStaffAccountFromPractitioner(practitioner, roleSlugs, fallbackRole);
      if (fallbackRole && snapshot.status === 'Inactive') {
        snapshot = {
          status: 'Active',
          role: fallbackRole,
          heldRoles: normalizeHeldRoles([fallbackRole]) as Role[],
        };
      }
      applyStaffAccountSnapshot(snapshot);
      return snapshot;
    },
    [applyStaffAccountSnapshot, medplum, user.id],
  );

  const isClientUser = user.role === 'client';

  useEffect(() => {
    // Load PractitionerRole codes for every staff user. Gating on treating
    // capability hid supervisor/provider under a unify-assistant primary.
    if (isClientUser) {
      setProviderPractitioner(null);
      setPractitionerProfileLoadFailed(false);
      setPractitionerOrgRoleSlugs([]);
      setAvatarFile(null);
      setLicenses([]);
      setCertifications([]);
      setDeaCredentials([]);
      setSupplementalCredentials([]);
      setEducationEntries([]);
      setTrainings([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setPractitionerProfileLoadFailed(false);
        medplum.invalidateSearches('Practitioner');
        medplum.invalidateSearches('PractitionerRole');
        const practitioner = await medplum.readResource('Practitioner', user.id, PROFILE_RESOURCE_READ_OPTIONS);
        if (cancelled) {
          return;
        }

        const roleSlugs = await loadPractitionerRoleSlugs(medplum, practitioner);
        if (cancelled) {
          return;
        }
        setPractitionerOrgRoleSlugs(roleSlugs);

        const parsedPreferred = resolvePractitionerPreferredNamesForForm(practitioner);
        const staffPreferred = {
          first: parsedPreferred.preferredFirstName,
          last: parsedPreferred.preferredLastName,
        };
        preferredFirstDraftRef.current = staffPreferred.first;
        preferredLastDraftRef.current = staffPreferred.last;
        setStaffPreferredInputs(staffPreferred);

        const qualifications = practitioner.qualification ?? [];
        setProviderPractitioner(practitioner);
        setAvatarFile(null);
        setAvatarUrl(profilePhotoUrl(practitioner.photo?.[0]) ?? null);
        setEducationEntries(
          qualifications
            .filter((qualification) => getQualificationCode(qualification) === 'education')
            .map((qualification, index) => ({
              id: qualification.id ?? `education-${index}`,
              institution: qualification.issuer?.display ?? '',
              degree: qualification.code?.text ?? '',
              graduationYear: qualification.period?.end ?? '',
            })),
        );
        setTrainings(
          qualifications
            .filter((qualification) => getQualificationCode(qualification) === 'training')
            .map((qualification, index) => ({
              id: qualification.id ?? `training-${index}`,
              trainingType: qualification.code?.text ?? '',
              completedDate: qualification.period?.start
                ? formatDateForDisplay(qualification.period.start)
                : '—',
            })),
        );
        setLicenses(
          qualifications
            .filter((qualification) => getQualificationCode(qualification) === 'license')
            .map((qualification, index) => ({
              id: qualification.id ?? `license-${index}`,
              jurisdictionState: getQualificationString(qualification, QUALIFICATION_EXT_URLS.jurisdictionState) ?? '',
              licenseName: qualification.code?.text ?? '',
              licenseNumber: getEditableIdentifierValue(qualification),
              expirationDate: qualification.period?.end ? formatDateForDisplay(qualification.period.end) : '—',
              licenseStatus: getQualificationString(qualification, QUALIFICATION_EXT_URLS.licenseStatus) ?? '',
              primaryLicense: getQualificationBoolean(qualification, QUALIFICATION_EXT_URLS.isPrimary) ?? false,
              lastModifiedDate: qualification.period?.end ? formatDateForDisplay(qualification.period.end) : '—',
              issueDate: qualification.period?.start ? formatDateForDisplay(qualification.period.start) : undefined,
              licenseType: getQualificationString(qualification, QUALIFICATION_EXT_URLS.licenseType),
              superseded: getQualificationBoolean(qualification, QUALIFICATION_EXT_URLS.superseded),
            })),
        );
        setCertifications(
          qualifications
            .filter((qualification) => getQualificationCode(qualification) === 'board-certification')
            .map((qualification, index) => ({
              id: qualification.id ?? `board-cert-${index}`,
              boardCertificationName: qualification.code?.text ?? '',
              certificationNumber: getEditableIdentifierValue(qualification),
              certificationDate: qualification.period?.start ? formatDateForDisplay(qualification.period.start) : '—',
              certificateFileName: '',
              certType: getQualificationString(qualification, QUALIFICATION_EXT_URLS.boardCertificationType),
            })),
        );
        setDeaCredentials(
          qualifications
            .filter((qualification) => getQualificationCode(qualification) === 'dea')
            .map((qualification, index) => ({
              id: qualification.id ?? `dea-${index}`,
              deaNumberName: qualification.code?.text ?? '',
              active: (getQualificationString(qualification, QUALIFICATION_EXT_URLS.licenseStatus) ?? 'active') !== 'inactive',
              expirationDate: qualification.period?.end ? formatDateForDisplay(qualification.period.end) : '—',
              drugSchedules: getQualificationString(qualification, QUALIFICATION_EXT_URLS.drugSchedules) ?? '',
              businessActivity: getQualificationString(qualification, QUALIFICATION_EXT_URLS.businessActivity) ?? '',
            })),
        );
        setSupplementalCredentials(
          qualifications
            .filter((qualification) => getQualificationCode(qualification) === 'supplemental')
            .map((qualification, index) => ({
              id: qualification.id ?? `supp-${index}`,
              trainingName: qualification.code?.text ?? '',
              completedDate: qualification.period?.start ? formatDateForDisplay(qualification.period.start) : '—',
              trainingType: getQualificationString(qualification, QUALIFICATION_EXT_URLS.trainingType) ?? '',
            })),
        );
      } catch (error) {
        if (!cancelled) {
          setPractitionerProfileLoadFailed(true);
          notifications.show({
            title: 'Could not load provider profile',
            message: error instanceof Error ? error.message : 'Failed to load provider details.',
            color: 'red',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isClientUser, medplum, user.id]);

  const providerClinical = useMemo(
    () => (providerPractitioner ? getClinical(providerPractitioner) : undefined),
    [providerPractitioner],
  );
  const legalName = providerPractitioner ? resolvePractitionerLegalName(providerPractitioner) : undefined;
  /** Header shows "Preferred Name (Legal Name)"; legal is dropped when there is no distinct nickname. */
  const headerName = useMemo(() => {
    if (!providerPractitioner) {
      return { primary: editedUser.name, legal: editedUser.legalName };
    }
    return formatPractitionerNameParts(providerPractitioner);
  }, [providerPractitioner, editedUser.name, editedUser.legalName]);
  const providerAddress = providerPractitioner?.address?.[0];
  const providerLanguages = (providerPractitioner?.communication ?? [])
    .map((concept) => concept.text ?? concept.coding?.[0]?.display ?? concept.coding?.[0]?.code ?? '')
    .filter(Boolean);
  const providerNpi = providerPractitioner ? getIdentifierValue(providerPractitioner, NPI_IDENTIFIER_SYSTEM) : '';

  useEffect(() => {
    if (!providerPractitioner) {
      profileHydratedVersionRef.current = undefined;
      return;
    }
    const versionKey = `${providerPractitioner.id}:${providerPractitioner.meta?.versionId ?? providerPractitioner.meta?.lastUpdated ?? 'initial'}`;
    if (profileHydratedVersionRef.current === versionKey) {
      return;
    }
    profileHydratedVersionRef.current = versionKey;
    const nick = resolvePractitionerPreferredNamesForForm(providerPractitioner);
    profileForm.setValues({
      preferredFirstName: nick.preferredFirstName,
      preferredLastName: nick.preferredLastName,
      phone: providerPractitioner.telecom?.find((entry) => entry.system === 'phone' && entry.use === 'work')?.value ?? '',
      street: providerAddress?.line?.[0] ?? '',
      city: providerAddress?.city ?? '',
      state: providerAddress?.state ?? '',
      zipCode: digitsOnly(providerAddress?.postalCode ?? '').slice(0, 5),
      languages: providerLanguages,
      bio: getBio(providerPractitioner) ?? '',
      pronouns: getPractitionerPronouns(providerPractitioner) ?? '',
      genderIdentity: getPractitionerGenderIdentity(providerPractitioner) ?? '',
      lgbtqiaIdentity: getPractitionerLgbtqiaIdentities(providerPractitioner) ?? '',
      religion: parseJsonArray(getPractitionerReligionSpirituality(providerPractitioner)),
      raceEthnicity: parseJsonArray(getPractitionerRaceEthnicity(providerPractitioner)),
      ageGroups: providerClinical?.ageGroups ?? [],
      specialties: providerClinical?.specialties ?? [],
      modalities: providerClinical?.modalities ?? [],
    });
    profileForm.resetDirty();
    clinicalForm.setValues({
      caqhId: getCaqhId(providerPractitioner) ?? '',
      caqhUsername: getCaqhUsername(providerPractitioner) ?? '',
      caqhPassword: getCaqhPassword(providerPractitioner) ?? '',
      lastCaqhAttestation: getCaqhAttestationDate(providerPractitioner) ?? null,
    });
    clinicalForm.resetDirty();
  }, [providerPractitioner, providerAddress, providerClinical, providerLanguages, profileForm, clinicalForm]);

  const providerCanSave = profileForm.isDirty() || roleSetDirty;
  const canEditAvatar =
    !isClientUser
    && Boolean(providerPractitioner)
    && !avatarSaving
    && canEditMemberProfile(editorPermissions, isSelf);

  /** Held functional roles — same source as the Permissions tab. */
  const providerHeaderRoleSlugs = heldRoles;

  const getStatusColor = (status: string) => {
    if (status === 'Active') return 'green';
    if (status === 'Pending') return 'yellow';
    return 'red';
  };

  const permissionsPanel = (
    <Box data-autosave-ignore="">
      <UserPermissionsTabPanel
        userId={editedUser.id}
        role={derivedPrimary}
        heldRoles={heldRoles}
        loginDefaultRole={editedUser.role}
        isSelf={isSelf}
        onLoginDefaultChange={(value) => { void handleDefaultRoleChange(value); }}
        loginDefaultSaving={defaultRoleSaving}
        practiceId={editedUser.practiceId}
        canEditUserRole={canEditUserRole}
        showRolePermissionsReference={showRolePermissionsReference}
        assignableRoles={assignableRoles}
        roleSaving={roleSaving}
        refreshToken={roleRefreshToken}
        onHeldRolesChange={(next) => {
          setHeldRoles(next);
          const primary = derivePrimaryRole(next);
          if (primary) {
            setEditedUser((prev) => ({ ...prev, role: primary }));
          }
          void persistRoleSet(next);
        }}
        practices={_practices}
        allUsers={_allUsers}
        canDeactivate={editorPermissions.edit_users === true && !isSelf}
        isInactive={editedUser.status === 'Inactive'}
        onDeactivate={() => {
          setPendingHeldRoles([]);
          setRoleTransitionMode('offboard');
          setRoleTransitionOpen(true);
        }}
        onReactivate={() => {
          setRoleTransitionMode('reactivate');
          setPendingHeldRoles([]);
          setRoleTransitionOpen(true);
        }}
      />
    </Box>
  );

  return (
    <Container
      size={mode === 'permissions' ? undefined : 'lg'}
      fluid={mode === 'permissions'}
      w={mode === 'permissions' ? '100%' : undefined}
      maw={mode === 'permissions' ? '100%' : undefined}
      py={mode === 'permissions' ? 0 : 'xl'}
      px={mode === 'permissions' ? 0 : undefined}
      style={mode === 'permissions' ? { maxWidth: '100%', width: '100%' } : undefined}
      onChangeCapture={autoSave ? (event) => {
        const target = event.target as HTMLElement | null;
        if (target?.closest?.('[data-autosave-ignore], [role="dialog"]')) return;
        scheduleAutosave();
      } : undefined}
    >
      <div ref={pageTopRef} tabIndex={-1} aria-hidden="true" />
      <Stack gap="lg">
        {mode === 'permissions' ? (
          permissionsPanel
        ) : (
          <>
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>User Profile</Title>
            <Text c="gray.6" size="sm" mt="xs">
              Manage user details, permissions, and account status
            </Text>
          </div>

          {!autoSave && (
            <Button
              leftSection={<IconDeviceFloppy size={18} />}
              onClick={() => void handleSave()}
              disabled={isProvider ? !providerCanSave : !(hasChanges || roleSetDirty)}
            >
              Save Changes
            </Button>
          )}
        </Group>

        {/* User Profile Card */}
        <Card withBorder>
          <Group gap="lg" align="flex-start">
            <input
              ref={avatarFileInputRef}
              data-autosave-ignore=""
              type="file"
              accept="image/*"
              disabled={!canEditAvatar}
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                if (!providerPractitioner?.id) {
                  notifications.show({
                    title: 'Cannot upload headshot',
                    message: 'Practitioner profile is still loading. Try again in a moment.',
                    color: 'red',
                  });
                  return;
                }
                const previousUrl = avatarUrl ?? profilePhotoUrl(providerPractitioner.photo?.[0]) ?? null;
                const objectUrl = URL.createObjectURL(file);
                setAvatarUrl(objectUrl);
                setAvatarFile(file);
                setAvatarSaving(true);
                void (async () => {
                  try {
                    await persistHeadshot(file);
                    notifications.show({
                      title: 'Headshot saved',
                      message: 'Professional headshot was updated.',
                      color: 'green',
                    });
                  } catch (error) {
                    setAvatarUrl(previousUrl);
                    setAvatarFile(null);
                    notifications.show({
                      title: 'Could not save headshot',
                      message: error instanceof Error ? error.message : 'Please try again.',
                      color: 'red',
                    });
                  } finally {
                    URL.revokeObjectURL(objectUrl);
                    setAvatarSaving(false);
                  }
                })();
              }}
            />
            <div
              style={{
                position: 'relative',
                cursor: avatarSaving ? 'wait' : canEditAvatar ? 'pointer' : 'default',
                borderRadius: 'var(--mantine-radius-xl)',
                overflow: 'hidden',
                opacity: avatarSaving ? 0.7 : 1,
              }}
              onClick={() => {
                if (canEditAvatar) avatarFileInputRef.current?.click();
              }}
              role="button"
              tabIndex={canEditAvatar ? 0 : -1}
              aria-disabled={!canEditAvatar}
              onKeyDown={(e) => {
                if (canEditAvatar && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  avatarFileInputRef.current?.click();
                }
              }}
              aria-label="Upload profile photo"
              aria-busy={avatarSaving}
            >
              <Avatar
                size="xl"
                radius="xl"
                color="orange"
                src={avatarUrl ?? profilePhotoUrl(providerPractitioner?.photo?.[0])}
              >
                {headerName.primary
                  .split(' ')
                  .map((n) => n[0])
                  .filter(Boolean)
                  .join('')}
              </Avatar>
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: 'var(--mantine-radius-xl)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0, 0, 0, 0.4)',
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.opacity = '1'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.opacity = '0'; }}
              >
                <IconCamera size={24} color="white" />
              </div>
            </div>

            <Stack gap="md" style={{ flex: 1 }}>
              <Group justify="space-between" align="flex-start">
                <div>
                  <Title order={3}>
                    {isClientUser ? (
                      <Anchor component={Link} to={`/dashboard/clients/${user.id}`} c="gold.7" inherit>
                        <PractitionerNameText
                          inherit
                          fw="inherit"
                          fz="inherit"
                          primary={headerName.primary || '—'}
                          legal={headerName.legal}
                        />
                      </Anchor>
                    ) : (
                      <PractitionerNameText
                        inherit
                        fw="inherit"
                        fz="inherit"
                        primary={headerName.primary || '—'}
                        legal={headerName.legal}
                      />
                    )}
                  </Title>
                  <Text c="gray.6" size="sm" mt="xs">
                    {editedUser.email}
                  </Text>
                  {isProvider && (
                    <Text c="gray.6" size="xs" mt={4}>
                      Professional headshot
                      <Text component="span" c="red" ml={4}>
                        *
                      </Text>
                      {' — '}
                      {avatarSaving
                        ? 'Saving…'
                        : avatarFile
                          ? avatarFile.name
                          : 'click the photo to upload or update'}
                    </Text>
                  )}
                </div>

                <Badge
                  color={getStatusColor(editedUser.status)}
                  size="lg"
                  variant="light"
                >
                  {editedUser.status}
                </Badge>
              </Group>

              <Grid gutter="md" pt="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Group gap="xs">
                    <IconBuilding size={16} color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="gray.6">
                      Practice:
                    </Text>
                    <Text size="sm" fw={500}>
                      {editedUser.practiceName}
                    </Text>
                  </Group>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Group gap="xs">
                    <IconCalendar size={16} color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="gray.6">
                      Joined:
                    </Text>
                    <Text size="sm" fw={500}>
                      {formatUsDateShort(joinedAt ?? editedUser.createdAt) || '—'}
                    </Text>
                  </Group>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Group gap="xs">
                    <IconActivity size={16} color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="gray.6">
                      Last Active:
                    </Text>
                    <Text size="sm" fw={500}>
                      {formatLastActive(isSelf ? (selfLastActive ?? editedUser.lastActive) : editedUser.lastActive)}
                    </Text>
                  </Group>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Group gap="xs">
                    <IconShield size={16} color="var(--mantine-color-gray-6)" />
                    <Text size="sm" c="gray.6">
                      {isClientUser ? 'Role:' : 'Roles:'}
                    </Text>
                    <Text size="sm" fw={500}>
                      {isClientUser
                        ? ROLE_LABELS[editedUser.role]
                        : providerHeaderRoleSlugs.length > 0
                          ? formatCollapsedRoleLabels(providerHeaderRoleSlugs)
                          : 'None'}
                    </Text>
                  </Group>
                </Grid.Col>
              </Grid>
            </Stack>
          </Group>
        </Card>

        {/* Tabs */}
        { !isClientUser && !providerPractitioner && !practitionerProfileLoadFailed ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : (
          <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="profile" leftSection={<IconUser size={16} />}>
              Profile
            </Tabs.Tab>
            {!isSelf ? (
              <Tabs.Tab value="permissions" leftSection={<IconShield size={16} />}>
                Permissions
              </Tabs.Tab>
            ) : null}
            {showClinicalInfoTab && (
              <Tabs.Tab value="clinical" leftSection={<IconStethoscope size={16} />}>
                <Group gap="xs" wrap="nowrap">
                  Clinical Info
                  <LocalhostOnlyBadge />
                </Group>
              </Tabs.Tab>
            )}
            {showAppointmentTypesTab && (
              <Tabs.Tab value="appointmentTypes" leftSection={<IconCalendar size={16} />}>
                <Group gap="xs" wrap="nowrap">
                  Appointment Types
                  <LocalhostOnlyBadge />
                </Group>
              </Tabs.Tab>
            )}
          </Tabs.List>

          {/* Profile Tab: Provider profile form */}
          <Tabs.Panel value="profile" pt="lg" keepMounted>
            {isProvider ? (
              <Paper p={32} withBorder radius="md" shadow="sm">
                <Stack gap={24}>
                  <Text size="sm" c="dimmed">
                    Required fields are marked with{' '}
                    <Text component="span" c="red" inherit>
                      *
                    </Text>
                  </Text>
                  <Stack gap={6}>
                    <Group grow align="flex-start">
                      <TextInput
                        label="Legal first name"
                        value={legalName?.given || '—'}
                        readOnly
                        variant="filled"
                        rightSection={<IconLock size={16} aria-hidden />}
                        rightSectionPointerEvents="none"
                      />
                      <TextInput
                        label="Legal last name"
                        value={legalName?.family || '—'}
                        readOnly
                        variant="filled"
                        rightSection={<IconLock size={16} aria-hidden />}
                        rightSectionPointerEvents="none"
                      />
                    </Group>
                  </Stack>

                  <Group grow align="flex-start">
                    <TextInput
                      label="Date of Birth"
                      value={providerPractitioner?.birthDate ?? '—'}
                      readOnly
                      variant="filled"
                      rightSection={<IconLock size={16} aria-hidden />}
                      rightSectionPointerEvents="none"
                    />
                    <TextInput
                      label="NPI"
                      value={providerNpi || '—'}
                      readOnly
                      variant="filled"
                      rightSection={<IconLock size={16} aria-hidden />}
                      rightSectionPointerEvents="none"
                    />
                  </Group>

                  <ProviderProfileSettingsFields form={profileForm} />
                </Stack>
              </Paper>
            ) : (
              <Paper p={32} withBorder radius="md" shadow="sm">
                <Stack gap={24}>
                  {!isClientUser && (
                    <Group grow align="flex-start">
                      <TextInput
                        label="Legal first name"
                        value={legalName?.given || '—'}
                        readOnly
                        variant="filled"
                        rightSection={<IconLock size={16} aria-hidden />}
                        rightSectionPointerEvents="none"
                      />
                      <TextInput
                        label="Legal last name"
                        value={legalName?.family || '—'}
                        readOnly
                        variant="filled"
                        rightSection={<IconLock size={16} aria-hidden />}
                        rightSectionPointerEvents="none"
                      />
                    </Group>
                  )}
                  <Group grow>
                    <TextInput
                      key={`pfn-${user.id}-${staffPreferredInputs.first}`}
                      label="Preferred First Name"
                      placeholder="Preferred First Name"
                      defaultValue={staffPreferredInputs.first}
                      onChange={(e) => {
                        preferredFirstDraftRef.current = e.currentTarget.value;
                        setHasChanges((prev) => prev || true);
                      }}
                    />
                    <TextInput
                      key={`pln-${user.id}-${staffPreferredInputs.last}`}
                      label="Preferred Last Name"
                      placeholder="Preferred Last Name"
                      defaultValue={staffPreferredInputs.last}
                      onChange={(e) => {
                        preferredLastDraftRef.current = e.currentTarget.value;
                        setHasChanges((prev) => prev || true);
                      }}
                    />
                  </Group>
                  {user.aliases && user.aliases.length > 0 ? (
                    <Text size="sm" c="dimmed">
                      Other names or aliases: {user.aliases.join(', ')}
                    </Text>
                  ) : null}
                  <DraftDobAndZip
                    ref={dobZipDraftRef}
                    userId={user.id}
                    initialClinical={user.clinical}
                    onDirty={markDraftDirty}
                  />
                  <Textarea
                    key={`bio-${user.id}-${user.bio ?? ''}`}
                    label="Bio"
                    placeholder="Bio"
                    minRows={10}
                    maxRows={24}
                    defaultValue={user.bio ?? ''}
                    onChange={(e) => {
                      bioDraftRef.current = e.currentTarget.value;
                      setHasChanges((prev) => prev || true);
                    }}
                  />
                  <Divider color="#E5E7EB" />
                  <DraftProfileDemographics
                    ref={demographicsDraftRef}
                    userId={user.id}
                    initial={user.clinical?.demographics}
                    onDirty={markDraftDirty}
                  />
                </Stack>
              </Paper>
            )}
            {user.role !== 'client' ? (
              <Card withBorder mt="lg">
                <div>
                  <Group gap="sm" align="center" wrap="wrap">
                    <Title order={4}>Practice Locations</Title>
                  </Group>
                  <Text size="sm" c="gray.6" mt="xs">
                    Select the locations that you go to
                  </Text>
                </div>
                <LocationsPanel
                  key={`${locationsOrgId}-${editedUser.id}`}
                  organizationId={locationsOrgId}
                  practitionerId={editedUser.id}
                  selectedLocationIds={selectedLocationIds}
                  onInit={handleLocationsInit}
                  onToggle={handleLocationToggle}
                />
              </Card>
            ) : null}
            <ProfileSignatureSettings />
          </Tabs.Panel>

          {!isSelf ? (
            <Tabs.Panel value="permissions" pt="lg">
              {activeTab === 'permissions' ? permissionsPanel : null}
            </Tabs.Panel>
          ) : null}

          {/* Clinical Info Tab (providers only) — CAQH, Licenses, Enrollments */}
          {showClinicalInfoTab ? (
          <Tabs.Panel value="clinical" pt="lg" keepMounted>
            <Stack gap="lg">
              <Card withBorder data-autosave-ignore="">
                <Group justify="space-between" mb="md" align="flex-start">
                  <div>
                    <Title order={4}>CAQH Information</Title>
                    <Text size="sm" c="gray.6" mt="xs">
                      CAQH ProView credentials and attestation dates. Required fields are marked with{' '}
                      <Text component="span" c="red" inherit>
                        *
                      </Text>
                      .
                    </Text>
                  </div>
                  <Button
                    leftSection={<IconDeviceFloppy size={16} />}
                    onClick={() => saveCaqhInformation()}
                    loading={caqhSaving}
                    disabled={!clinicalForm.isDirty()}
                  >
                    Save
                  </Button>
                </Group>
                <ProviderCaqhSettingsFields form={clinicalForm} />
              </Card>

              <Modal
                title={editingEduId ? 'Edit Education' : 'New Education'}
                opened={eduModalOpen}
                onClose={closeEducationModal}
                size="lg"
              >
                <Stack gap="md">
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Institution"
                        placeholder="Institution"
                        {...educationModalForm.getInputProps('institution')}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Degree"
                        placeholder="Degree"
                        {...educationModalForm.getInputProps('degree')}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Graduation Year"
                        placeholder="YYYY"
                        {...educationModalForm.getInputProps('graduationYear')}
                        onChange={(event) => {
                          educationModalForm.setFieldValue(
                            'graduationYear',
                            event.currentTarget.value.replace(/\D/g, '').slice(0, 4),
                          );
                        }}
                      />
                    </Grid.Col>
                  </Grid>
                </Stack>
                <Group justify="flex-end" gap="sm" mt="xl">
                  <Button variant="default" onClick={closeEducationModal}>
                    Cancel
                  </Button>
                  <Button onClick={() => saveEducation()}>
                    {editingEduId ? 'Save changes' : 'Add Education'}
                  </Button>
                </Group>
              </Modal>

              <Card withBorder>
                <Group justify="space-between" mb="md">
                  <div>
                    <Title order={4}>Education</Title>
                    <Text size="sm" c="gray.6" mt="xs">
                      Education history captured during onboarding.
                    </Text>
                  </div>
                  <Button leftSection={<IconPlus size={16} />} onClick={openAddEducation}>
                    Add Education
                  </Button>
                </Group>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Institution</Table.Th>
                      <Table.Th>Degree</Table.Th>
                      <Table.Th>Graduation Year</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {educationEntries.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={3}>
                          <Text c="gray.6" size="sm" ta="center" py="lg">
                            No education entries yet
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      educationEntries.map((education) => (
                        <Table.Tr
                          key={education.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => openEditEducation(education)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              openEditEducation(education);
                            }
                          }}
                        >
                          <Table.Td>
                            <Text size="sm">{education.institution || '—'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{education.degree || '—'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{education.graduationYear || '—'}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    )}
                  </Table.Tbody>
                </Table>
              </Card>

              <Card withBorder>
                <Box mb="md">
                  <Group gap={6}>
                    <Title order={4}>Licenses</Title>
                    <Tooltip label={CREDENTIAL_BILLING_HINTS.license} withArrow>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        aria-label={CREDENTIAL_BILLING_HINTS.license}
                      >
                        <IconInfoCircle size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                  <Text size="sm" c="gray.6" mt="xs">
                    Provider license information
                  </Text>
                </Box>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Jurisdiction State</Table.Th>
                      <Table.Th>License Name</Table.Th>
                      <Table.Th>License Number</Table.Th>
                      <Table.Th>Expiration Date</Table.Th>
                      <Table.Th>License Status</Table.Th>
                      <Table.Th>Primary License</Table.Th>
                      <Table.Th>Last Modified Date</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {licenses.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={7}>
                          <Text c="gray.6" size="sm" ta="center" py="lg">
                            No licenses yet
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      licenses.map((license) => (
                        <Table.Tr
                          key={license.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => openEditLicense(license)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openEditLicense(license);
                            }
                          }}
                        >
                          <Table.Td>
                            <Text size="sm">{license.jurisdictionState}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{license.licenseName}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{license.licenseNumber}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{license.expirationDate}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              color={license.licenseStatus === 'Active' ? 'green' : 'gray'}
                              size="sm"
                              variant="light"
                            >
                              {license.licenseStatus}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            {license.primaryLicense ? (
                              <Text size="sm" c="green">✓</Text>
                            ) : (
                              <Text size="sm" c="gray.5">—</Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{license.lastModifiedDate}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    )}
                  </Table.Tbody>
                </Table>
              </Card>

              <Modal
                title={editingLicenseId ? 'Edit License' : 'New License'}
                opened={showAddLicenseModal}
                onClose={closeAddLicenseModal}
                size="lg"
              >
                <Stack gap="md">
                  <Text size="sm" fw={600}>
                    Summary
                  </Text>
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="License Name"
                        placeholder="License name"
                        {...licenseModalForm.getInputProps('licenseName')}
                        withAsterisk
                        required
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="License Number"
                        placeholder="License number"
                        {...licenseModalForm.getInputProps('licenseNumber')}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Select
                        label="Jurisdiction State"
                        placeholder="--None--"
                        data={US_STATES}
                        value={licenseModalForm.values.jurisdictionState}
                        onChange={(value) => licenseModalForm.setFieldValue('jurisdictionState', value)}
                        clearable
                        error={licenseModalForm.errors.jurisdictionState}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Select
                        label="License Status"
                        placeholder="Select status"
                        data={[
                          { value: 'Active', label: 'Active' },
                          { value: 'Inactive', label: 'Inactive' },
                          { value: 'Pending', label: 'Pending' },
                          { value: 'Expired', label: 'Expired' },
                        ]}
                        value={licenseModalForm.values.licenseStatus || null}
                        onChange={(v) => licenseModalForm.setFieldValue('licenseStatus', v ?? '')}
                        clearable
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <DateInput
                        label="Issue Date"
                        placeholder="Select a date"
                        value={licenseModalForm.values.issueDate ? calendarDateFromPickerValue(licenseModalForm.values.issueDate) : null}
                        onChange={(v) => licenseModalForm.setFieldValue('issueDate', toIsoFromDateInput(v))}
                        leftSection={<IconCalendarEvent size={16} />}
                        clearable
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <DateInput
                        label="Expiration Date"
                        placeholder="Select a date"
                        value={licenseModalForm.values.expirationDate ? calendarDateFromPickerValue(licenseModalForm.values.expirationDate) : null}
                        onChange={(v) => licenseModalForm.setFieldValue('expirationDate', toIsoFromDateInput(v))}
                        leftSection={<IconCalendarEvent size={16} />}
                        clearable
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Select
                        label="License Type"
                        placeholder="--None--"
                        data={LICENSE_TYPES}
                        value={licenseModalForm.values.licenseType}
                        onChange={(value) => licenseModalForm.setFieldValue('licenseType', value)}
                        clearable
                      />
                    </Grid.Col>
                  </Grid>
                  <Divider my="sm" />
                  <Stack gap="sm">
                    <Checkbox
                      label="Primary License"
                      checked={licenseModalForm.values.isPrimary}
                      onChange={(e) => licenseModalForm.setFieldValue('isPrimary', e.currentTarget.checked)}
                    />
                    <Checkbox
                      label="Superseded"
                      checked={licenseModalForm.values.superseded}
                      onChange={(e) => licenseModalForm.setFieldValue('superseded', e.currentTarget.checked)}
                    />
                  </Stack>
                </Stack>
                <Group justify="flex-end" gap="sm" mt="xl">
                  <Button variant="default" onClick={closeAddLicenseModal}>
                    Cancel
                  </Button>
                  <Button onClick={() => saveLicense()} loading={licenseSaving}>
                    {editingLicenseId ? 'Save changes' : 'Add License'}
                  </Button>
                </Group>
              </Modal>

              <Card withBorder>
                <Box mb="md">
                  <Group gap={6}>
                    <Title order={4}>Certifications</Title>
                    <Tooltip label={CREDENTIAL_BILLING_HINTS.certification} withArrow>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        aria-label={CREDENTIAL_BILLING_HINTS.certification}
                      >
                        <IconInfoCircle size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                  <Text size="sm" c="gray.6" mt="xs">
                    Board certifications and certificate documents
                  </Text>
                </Box>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Board Certification Name</Table.Th>
                      <Table.Th>Certification Number</Table.Th>
                      <Table.Th>Certification Date</Table.Th>
                      <Table.Th>Certificate</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {certifications.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={4}>
                          <Text c="gray.6" size="sm" ta="center" py="lg">
                            No certifications yet
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      certifications.map((cert) => (
                        <Table.Tr
                          key={cert.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => openEditCertification(cert)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openEditCertification(cert);
                            }
                          }}
                        >
                          <Table.Td>
                            <Text size="sm">{cert.boardCertificationName}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{cert.certificationNumber || '—'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{cert.certificationDate}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{cert.certificateFileName || '—'}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    )}
                  </Table.Tbody>
                </Table>
              </Card>

              <Modal
                title={editingCertificationId ? 'Edit Certification' : 'New Certification'}
                opened={showAddCertificationModal}
                onClose={closeAddCertificationModal}
                size="lg"
              >
                <Stack gap="md">
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Board Certification Name"
                        placeholder="e.g. American Board of Professional Psychology"
                        {...boardCertModalForm.getInputProps('boardCertificationName')}
                        withAsterisk
                        required
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Certification Number"
                        placeholder="Certification number"
                        {...boardCertModalForm.getInputProps('certificationNumber')}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <DateInput
                        label="Certification Date"
                        placeholder="Select a date"
                        value={boardCertModalForm.values.certificationDate ? calendarDateFromPickerValue(boardCertModalForm.values.certificationDate) : null}
                        onChange={(v) => boardCertModalForm.setFieldValue('certificationDate', toIsoFromDateInput(v))}
                        leftSection={<IconCalendarEvent size={16} />}
                        clearable
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Select
                        label="Board Certification Type"
                        placeholder="Select type"
                        data={BOARD_CERT_TYPE_OPTIONS}
                        value={boardCertModalForm.values.certType}
                        onChange={(value) => boardCertModalForm.setFieldValue('certType', value)}
                        clearable
                      />
                    </Grid.Col>
                  </Grid>
                  <div>
                    <Text size="sm" fw={500} mb={4} component="label" display="block">
                      Certificate (image or PDF)
                    </Text>
                    {editingCertificationId && certifications.find((c) => c.id === editingCertificationId)?.certificateFileName && (
                      <Text size="xs" c="gray.6" mb="xs">
                        Current: {certifications.find((c) => c.id === editingCertificationId)?.certificateFileName}
                      </Text>
                    )}
                    <input
                      ref={certificationFileInputRef}
                      type="file"
                      accept="application/pdf,image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => boardCertModalForm.setFieldValue('certificateFile', e.target.files?.[0] ?? null)}
                    />
                    <Paper
                      p="xl"
                      withBorder
                      style={{
                        border: '2px dashed var(--mantine-color-gray-4)',
                        textAlign: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() => certificationFileInputRef.current?.click()}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          certificationFileInputRef.current?.click();
                        }
                      }}
                      aria-label="Drop files here or click to upload certificate"
                    >
                      <Stack gap="xs" align="center">
                        <IconEdit size={40} color="var(--mantine-color-gray-5)" />
                        <div>
                          <Text size="sm" fw={600}>
                            Drop files here or click to upload
                          </Text>
                          <Text size="xs" c="gray.6">
                            PDF, PNG, JPG up to 10MB
                          </Text>
                        </div>
                        {boardCertModalForm.values.certificateFile && (
                          <Text size="xs" c="dimmed" mt="xs">
                            {boardCertModalForm.values.certificateFile.name}
                          </Text>
                        )}
                      </Stack>
                    </Paper>
                  </div>
                </Stack>
                <Group justify="flex-end" gap="sm" mt="xl">
                  <Button variant="default" onClick={closeAddCertificationModal}>
                    Cancel
                  </Button>
                  <Button onClick={() => saveCertification()} loading={certificationSaving}>
                    {editingCertificationId ? 'Save changes' : 'Add Certification'}
                  </Button>
                </Group>
              </Modal>

              <Card withBorder>
                <Box mb="md">
                  <Group gap={6}>
                    <Title order={4}>DEA Credentials</Title>
                    <Tooltip label={CREDENTIAL_BILLING_HINTS.dea} withArrow>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        aria-label={CREDENTIAL_BILLING_HINTS.dea}
                      >
                        <IconInfoCircle size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                  <Text size="sm" c="gray.6" mt="xs">
                    DEA numbers and controlled substance schedules.
                  </Text>
                </Box>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>DEA Number Name</Table.Th>
                      <Table.Th>Active</Table.Th>
                      <Table.Th>Expiration Date</Table.Th>
                      <Table.Th>Drug Schedules</Table.Th>
                      <Table.Th>Business Activity</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {deaCredentials.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={5}>
                          <Text c="gray.6" size="sm" ta="center" py="lg">
                            No DEA credentials yet
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      deaCredentials.map((credential) => (
                        <Table.Tr
                          key={credential.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => openEditDea(credential)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              openEditDea(credential);
                            }
                          }}
                        >
                          <Table.Td>
                            <Text size="sm">{credential.deaNumberName}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{credential.active ? 'Yes' : 'No'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{credential.expirationDate}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{credential.drugSchedules || '—'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{credential.businessActivity || '—'}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    )}
                  </Table.Tbody>
                </Table>
              </Card>

              <Modal
                title={editingDeaId ? 'Edit DEA Credential' : 'New DEA Credential'}
                opened={deaModalOpen}
                onClose={closeDeaModal}
                size="lg"
              >
                <Stack gap="md">
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="DEA Number Name"
                        placeholder="DEA number name"
                        {...deaModalForm.getInputProps('deaNumberName')}
                        withAsterisk
                        required
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Switch
                        label="Active"
                        checked={deaModalForm.values.active}
                        onChange={(event) => deaModalForm.setFieldValue('active', event.currentTarget.checked)}
                        mt="xl"
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <DateInput
                        label="Expiration Date"
                        placeholder="Select a date"
                        value={deaModalForm.values.expirationDate ? calendarDateFromPickerValue(deaModalForm.values.expirationDate) : null}
                        onChange={(value) => deaModalForm.setFieldValue('expirationDate', toIsoFromDateInput(value))}
                        leftSection={<IconCalendarEvent size={16} />}
                        clearable
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Drug Schedules"
                        placeholder="e.g. II-V"
                        {...deaModalForm.getInputProps('drugSchedules')}
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <TextInput
                        label="Business Activity"
                        placeholder="Business activity"
                        {...deaModalForm.getInputProps('businessActivity')}
                      />
                    </Grid.Col>
                  </Grid>
                </Stack>
                <Group justify="flex-end" gap="sm" mt="xl">
                  <Button variant="default" onClick={closeDeaModal}>
                    Cancel
                  </Button>
                  <Button onClick={() => saveDea()} loading={deaSaving}>
                    {editingDeaId ? 'Save changes' : 'Add DEA Credential'}
                  </Button>
                </Group>
              </Modal>

              <Card withBorder>
                <Group justify="space-between" mb="md">
                  <div>
                    <Title order={4}>Specialty Training</Title>
                    <Text size="sm" c="gray.6" mt="xs">
                      Fellowships, residencies, and other specialty training.
                    </Text>
                  </div>
                  <Button leftSection={<IconPlus size={16} />} onClick={openAddSupplemental}>
                    Add Specialty Training
                  </Button>
                </Group>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Training Name</Table.Th>
                      <Table.Th>Training Type</Table.Th>
                      <Table.Th>Completed Date</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {supplementalCredentials.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={3}>
                          <Text c="gray.6" size="sm" ta="center" py="lg">
                            No specialty training yet
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      supplementalCredentials.map((credential) => (
                        <Table.Tr
                          key={credential.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => openEditSupplemental(credential)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              openEditSupplemental(credential);
                            }
                          }}
                        >
                          <Table.Td>
                            <Text size="sm">{credential.trainingName}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{credential.trainingType || '—'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{credential.completedDate}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    )}
                  </Table.Tbody>
                </Table>
              </Card>

              <Modal
                title={editingSuppId ? 'Edit Specialty Training' : 'New Specialty Training'}
                opened={suppModalOpen}
                onClose={closeSupplementalModal}
                size="lg"
              >
                <Stack gap="md">
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Training Name"
                        placeholder="Training name"
                        {...supplementalModalForm.getInputProps('trainingName')}
                        withAsterisk
                        required
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Select
                        label="Training Type"
                        placeholder="Select training type"
                        data={SPECIALTY_TRAINING_TYPE_OPTIONS}
                        value={supplementalModalForm.values.trainingType}
                        onChange={(value) => supplementalModalForm.setFieldValue('trainingType', value)}
                        clearable
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <DateInput
                        label="Completed Date"
                        placeholder="Select a date"
                        value={supplementalModalForm.values.completedDate ? calendarDateFromPickerValue(supplementalModalForm.values.completedDate) : null}
                        onChange={(value) => supplementalModalForm.setFieldValue('completedDate', toIsoFromDateInput(value))}
                        leftSection={<IconCalendarEvent size={16} />}
                        clearable
                      />
                    </Grid.Col>
                  </Grid>
                </Stack>
                <Group justify="flex-end" gap="sm" mt="xl">
                  <Button variant="default" onClick={closeSupplementalModal}>
                    Cancel
                  </Button>
                  <Button onClick={() => saveSupplemental()} loading={supplementalSaving}>
                    {editingSuppId ? 'Save changes' : 'Add Specialty Training'}
                  </Button>
                </Group>
              </Modal>

              <Card withBorder>
                <Group justify="space-between" mb="md">
                  <div>
                    <Title order={4}>Completed Trainings</Title>
                    <Text size="sm" c="gray.6" mt="xs">
                      Telehealth, law and ethics, and other required trainings.
                    </Text>
                  </div>
                  <Button leftSection={<IconPlus size={16} />} onClick={openAddTraining}>
                    Add Training
                  </Button>
                </Group>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Training Type</Table.Th>
                      <Table.Th>Completed Date</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {trainings.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={2}>
                          <Text c="gray.6" size="sm" ta="center" py="lg">
                            No trainings yet
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      trainings.map((training) => (
                        <Table.Tr key={training.id}>
                          <Table.Td>
                            <Text size="sm">{training.trainingType}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{training.completedDate}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    )}
                  </Table.Tbody>
                </Table>
              </Card>

              <Modal
                title="New Training"
                opened={trainingModalOpen}
                onClose={closeTrainingModal}
                size="md"
              >
                <Stack gap="md">
                  <Select
                    label="Training Type"
                    placeholder="Select training type"
                    data={TRAINING_TYPE_OPTIONS}
                    value={trainingModalForm.values.trainingType || null}
                    onChange={(value) => trainingModalForm.setFieldValue('trainingType', value ?? '')}
                    error={trainingModalForm.errors.trainingType}
                    withAsterisk
                    required
                  />
                  <DateInput
                    label="Completed Date"
                    placeholder="Select a date"
                    value={trainingModalForm.values.completedDate ? calendarDateFromPickerValue(trainingModalForm.values.completedDate) : null}
                    onChange={(value) => trainingModalForm.setFieldValue('completedDate', toIsoFromDateInput(value))}
                    error={trainingModalForm.errors.completedDate}
                    leftSection={<IconCalendarEvent size={16} />}
                    clearable
                    withAsterisk
                    required
                  />
                </Stack>
                <Group justify="flex-end" gap="sm" mt="xl">
                  <Button variant="default" onClick={closeTrainingModal}>
                    Cancel
                  </Button>
                  <Button onClick={() => saveTraining()}>
                    Add Training
                  </Button>
                </Group>
              </Modal>
            </Stack>
          </Tabs.Panel>
          ) : null}

          {/* Appointment Types Tab (providers only) */}
          {showAppointmentTypesTab && (
            <Tabs.Panel value="appointmentTypes" pt="lg">
              <ProviderAppointmentTypesPanel practiceId={editedUser.practiceId} userId={editedUser.id} />
            </Tabs.Panel>
          )}
          </Tabs>
        )}
          </>
        )}

        {autoSave && <SaveStatusIndicator status={autosaveStatus} onRetry={retryAutosave} />}

        {/* Credential Form Modal */}
        <CredentialFormModal
          isOpen={isAddingCredential || !!editingCredential}
          onClose={() => {
            setIsAddingCredential(false);
            setEditingCredential(null);
          }}
          onAddCredential={handleAddCredential}
          onUpdateCredential={handleUpdateCredential}
          credential={editingCredential}
        />

        {/* Delete Credential Confirmation Dialog */}
        <DeleteConfirmationDialog
          isOpen={showDeleteDialog}
          onClose={() => {
            setShowDeleteDialog(false);
            setCredentialToDelete(null);
          }}
          onConfirm={() => {
            if (credentialToDelete) {
              setCredentialing(
                credentialing.filter((c) => c.id !== credentialToDelete)
              );
              setHasChanges(true);
              scheduleAutosave();
            }
            setShowDeleteDialog(false);
            setCredentialToDelete(null);
          }}
          title="Delete Insurance Credential"
          description="Are you sure you want to delete this insurance credential?"
          confirmText="Delete Credential"
        />

        <RoleTransitionModal
          opened={roleTransitionOpen}
          mode={roleTransitionMode}
          assignableRoles={assignableRoles}
          onClose={() => {
            setRoleTransitionOpen(false);
            setPendingHeldRoles(null);
            setRoleTransitionMode('role-change');
            if (!roleTransitionCommittedRef.current) {
              setHeldRoles(baselineHeldRoles);
              setEditedUser((prev) => ({
                ...prev,
                role: baselinePrimary,
              }));
            }
            roleTransitionCommittedRef.current = false;
          }}
          practitionerId={user.id}
          practitionerName={user.name}
          currentRole={baselinePrimary}
          targetRole={
            roleTransitionMode === 'offboard'
              ? baselinePrimary
              : (derivePrimaryRole(pendingHeldRoles ?? heldRoles) ?? derivedPrimary)
          }
          initialHeldRoles={
            roleTransitionMode === 'offboard' ? [] : (pendingHeldRoles ?? heldRoles)
          }
          baselineHeldRoles={baselineHeldRoles}
          allUsers={_allUsers}
          onCompleted={async (role) => {
            roleTransitionCommittedRef.current = true;
            const completedMode = roleTransitionMode;
            if (completedMode === 'reactivate') {
              const optimistic: StaffAccountSnapshot = {
                status: 'Active',
                role,
                heldRoles: normalizeHeldRoles([role]) as Role[],
              };
              applyStaffAccountSnapshot(optimistic);
              let snapshot = optimistic;
              try {
                snapshot = await refreshStaffAccountFromServer(role);
              } catch {
                /* keep optimistic snapshot */
              }
              await Promise.resolve(
                onUpdateUser(user.id, {
                  status: snapshot.status,
                  role: snapshot.role,
                  heldRoles: snapshot.heldRoles,
                }),
              );
              setRoleRefreshToken((n) => n + 1);
              setRoleTransitionOpen(false);
            } else if (completedMode === 'offboard') {
              let snapshot: StaffAccountSnapshot = {
                status: 'Inactive',
                role,
                heldRoles: [],
              };
              try {
                medplum.invalidateSearches('Practitioner');
                const practitioner = await medplum.readResource('Practitioner', user.id, PROFILE_RESOURCE_READ_OPTIONS);
                setProviderPractitioner(practitioner);
                const roleSlugs = await loadPractitionerRoleSlugs(medplum, practitioner);
                snapshot = deriveStaffAccountFromPractitioner(practitioner, roleSlugs, role);
              } catch {
                /* keep optimistic defaults */
              }
              applyStaffAccountSnapshot(snapshot);
              await Promise.resolve(
                onUpdateUser(user.id, {
                  status: snapshot.status,
                  role: snapshot.role,
                  heldRoles: snapshot.heldRoles,
                }),
              );
              setRoleTransitionOpen(false);
            } else {
              const nextHeld = normalizeHeldRoles(pendingHeldRoles ?? heldRoles) as Role[];
              await Promise.resolve(onUpdateUser(user.id, { role, heldRoles: nextHeld }));
              setEditedUser((prev) => ({ ...prev, role }));
              setHeldRoles(nextHeld);
              setBaselineHeldRoles(nextHeld);
              setPractitionerOrgRoleSlugs(nextHeld);
            }
            setHasChanges(false);
            if (completedMode !== 'reactivate') {
              setRoleRefreshToken((n) => n + 1);
            }
            setRoleTransitionMode('role-change');
            if (completedMode === 'reactivate' || completedMode === 'offboard') {
              scrollUserProfileToTop(pageTopRef.current);
            }
          }}
        />
      </Stack>
    </Container>
  );
}

function fhirSiteToPracticeLocation(loc: FhirLocation): PracticeLocation {
  const line = loc.address?.line ?? [];
  return {
    id: loc.id ?? '',
    name: loc.name ?? '',
    addressLine1: line[0] ?? '',
    addressLine2: line[1],
    city: loc.address?.city ?? '',
    state: loc.address?.state ?? '',
    zipCode: loc.address?.postalCode ?? '',
    phone: getLocationWorkPhone(loc),
    latitude: loc.position?.latitude,
    longitude: loc.position?.longitude,
    status: loc.status === 'inactive' ? 'inactive' : 'active',
  };
}

interface LocationsPanelProps {
  organizationId: string;
  practitionerId: string;
  selectedLocationIds: string[];
  onInit: (ids: string[]) => void;
  onToggle: (id: string, checked: boolean) => void;
}

function LocationsPanel({
  organizationId,
  practitionerId,
  selectedLocationIds,
  onInit,
  onToggle,
}: LocationsPanelProps): JSX.Element {
  const medplum = useMedplum();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sites, setSites] = useState<PracticeLocation[]>([]);
  /** After `onInit` runs for a given `organizationId|practitionerId`, do not run again for that key (avoids wiping checkboxes on refetch after save). */
  const onInitAppliedForKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    const loadKey = `${organizationId}|${practitionerId}`;

    if (!organizationId || !practitionerId) {
      setSites([]);
      setLoading(false);
      onInitAppliedForKeyRef.current = undefined;
      return;
    }

    const isFirstFetchForThisKey = onInitAppliedForKeyRef.current !== loadKey;
    if (isFirstFetchForThisKey) {
      setLoading(true);
    }
    setError(null);

    (async () => {
      try {
        const orgParam = `Organization/${organizationId}`;
        const [locList, roleList] = await Promise.all([
          medplum.searchResources('Location', { organization: orgParam, _count: '500' }),
          medplum
            .searchResources('PractitionerRole', {
              practitioner: `Practitioner/${practitionerId}`,
              _count: '20',
            })
            .catch(() => [] as PractitionerRole[]),
        ]);
        if (cancelled) return;
        const sitesOnly = locList.filter(isPracticeSiteLocation);
        setSites(sitesOnly.map(fhirSiteToPracticeLocation));

        const orgScopedRole = roleList.find((r) => r.organization?.reference === orgParam);
        const fromRole = (orgScopedRole?.location ?? [])
          .map((ref) => parseLocationRefId(ref))
          .filter((id): id is string => Boolean(id));

        if (onInitAppliedForKeyRef.current !== loadKey) {
          onInitAppliedForKeyRef.current = loadKey;
          onInit(fromRole);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // MedplumClient is stable; including it caused refetches after unrelated parent updates and wiped selections via onInit([]).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when org/practitioner identity changes
  }, [organizationId, practitionerId, onInit]);

  if (loading) {
    return (
      <Center py="md" mt="md">
        <Loader size="sm" />
      </Center>
    );
  }

  if (error) {
    return (
      <Text c="red" size="sm" mt="md">
        {error}
      </Text>
    );
  }

  if (sites.length === 0) {
    return (
      <Text c="gray.6" size="sm" mt="md">
        No locations available for this practice
      </Text>
    );
  }

  return (
    <Stack gap="md" mt="md">
      {sites.map((location) => (
        <Card key={location.id} withBorder p="md" style={{ position: 'relative' }}>
          <Checkbox
            checked={selectedLocationIds.includes(location.id)}
            onChange={(e) => {
              onToggle(location.id, e.currentTarget.checked);
            }}
            style={{ position: 'absolute', top: 16, right: 16 }}
            aria-label={`Select ${location.name}`}
          />
          <Group gap="md">
            <LocationTileMap location={location} size={160} />
            <div>
              <Text fw={500} size="sm">
                {location.name}
              </Text>
              <Text size="xs" c="gray.6" mt="xs">
                {location.addressLine1 ?? (location as { address?: string }).address ?? ''}
                {location.addressLine2 ? `, ${location.addressLine2}` : ''}
              </Text>
              <Text size="xs" c="gray.6">
                {location.city}, {location.state} {location.zipCode}
              </Text>
              {location.phone && (
                <Text size="xs" c="gray.6" mt="xs">
                  {location.phone}
                </Text>
              )}
            </div>
          </Group>
        </Card>
      ))}
    </Stack>
  );
}

type ProviderSessionRow = AppointmentSessionType & { enabledForProvider: boolean };
type ProviderMeetingRow = CatalogMeetingType & { enabledForProvider: boolean };
type ProviderTimeEntryRow = CatalogTimeEntryType & { enabledForProvider: boolean };

function ProviderAppointmentTypesPanel({ practiceId, userId }: { practiceId: string; userId: string }) {
  // Read-only: appointment-type access is delegated by a Unify admin from Calendar settings.
  // Show only the types this provider is authorized for.
  const rows = useProviderSessionTypeEnablement(practiceId, userId).filter((r) => r.enabledForProvider);
  const meetingRows = useProviderMeetingTypeEnablement(practiceId, userId).filter((r) => r.enabledForProvider);
  const timeEntryRows = useProviderTimeEntryTypeEnablement(practiceId, userId).filter((r) => r.enabledForProvider);
  const columns: UnifyTableColumn<ProviderSessionRow>[] = [
    {
      key: 'description',
      label: 'Description',
      getValue: (row) => row.description,
      render: (row) => <Text size="sm">{row.description}</Text>,
    },
    {
      key: 'duration',
      label: 'Duration',
      getValue: (row) => String(row.durationMinutes),
      render: (row) => <Text size="sm">{row.durationMinutes} min</Text>,
    },
    {
      key: 'rate',
      label: 'Rate',
      getValue: (row) => String(row.rate),
      render: (row) => <Text size="sm">{row.cptCode ? `$${row.rate}` : '—'}</Text>,
    },
    {
      key: 'cptCode',
      label: 'CPT Code',
      getValue: (row) => row.cptCode,
      render: (row) => <Text size="sm">{row.cptCode || '—'}</Text>,
    },
  ];
  const meetingColumns: UnifyTableColumn<ProviderMeetingRow>[] = [
    {
      key: 'description',
      label: 'Description',
      getValue: (row) => row.description,
      render: (row) => <Text size="sm">{row.description}</Text>,
    },
    {
      key: 'status',
      label: 'Status',
      width: 180,
      align: 'right',
      pinRight: true,
      getValue: (row) => (row.active ? 'ACTIVE' : 'INACTIVE'),
      render: (row) => (
        <Badge color={row.active ? 'green' : 'gray'} variant="light" size="sm">
          {row.active ? 'ACTIVE' : 'INACTIVE'}
        </Badge>
      ),
    },
    {
      key: 'defaultDuration',
      label: 'Default duration',
      width: 160,
      align: 'center',
      getValue: (row) => String(row.defaultDurationMinutes),
      render: (row) => (
        <Text size="sm" ta="center">
          {row.defaultDurationMinutes} min
        </Text>
      ),
    },
  ];
  const timeEntryColumns: UnifyTableColumn<ProviderTimeEntryRow>[] = [
    {
      key: 'description',
      label: 'Description',
      getValue: (row) => row.description,
      render: (row) => <Text size="sm">{row.description}</Text>,
    },
    {
      key: 'status',
      label: 'Status',
      width: 180,
      align: 'right',
      pinRight: true,
      getValue: (row) => (row.active ? 'ACTIVE' : 'INACTIVE'),
      render: (row) => (
        <Badge color={row.active ? 'green' : 'gray'} variant="light" size="sm">
          {row.active ? 'ACTIVE' : 'INACTIVE'}
        </Badge>
      ),
    },
    {
      key: 'defaultDuration',
      label: 'Default duration',
      width: 160,
      align: 'center',
      getValue: (row) => String(row.defaultDurationMinutes),
      render: (row) => (
        <Text size="sm" ta="center">
          {row.defaultDurationMinutes} min
        </Text>
      ),
    },
  ];

  return (
    <Stack gap="lg">
      <Text size="sm" c="dimmed">
        Appointment types authorized for this provider. Access is managed by a Unify
        administrator under Calendar settings.
      </Text>
      <UnifyTable
        title="Provider Appointment Types"
        subtitle="Appointment types this provider is authorized to use"
        columns={columns}
        data={rows}
        getRowKey={(row) => row.id}
        sortable
        searchable
        searchPlaceholder="Search appointment types"
      />
      <UnifyTable
        title="Meeting types"
        subtitle="Meeting types this provider is authorized to use"
        columns={meetingColumns}
        data={meetingRows}
        getRowKey={(row) => row.id}
        sortable
        searchable
        searchPlaceholder="Search meeting types"
      />
      <UnifyTable
        title="Time entry types"
        subtitle="Time entry types this provider is authorized to use"
        columns={timeEntryColumns}
        data={timeEntryRows}
        getRowKey={(row) => row.id}
        sortable
        searchable
        searchPlaceholder="Search time entry types"
      />
    </Stack>
  );
}
